import { useMemo, useState, useEffect } from 'react';
import { ProjectType, performAction, deleteThread, type CatalogMode } from '../api/sophia';
import { CATALOG_MODES, getCatalogModeLabel } from '../types/catalogMode';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { createNewProject, loadProject } from '../features/thread/threadSlice';
import { AddProductModal } from './AddProductModal';
import OptimizationFailureCard from './OptimizationFailureCard';
import { NazioneFilter, ShopPicker, matchesShop, nazioniOf, type ShopOption } from './ShopPicker';

const SHOP_MAPPING_STORAGE_PREFIX = 'mp-shop-mapping:';

function getConsegnaDisplayLabel(consegna: string): string {
  return consegna;
}

function persistMerchandisingShopMapping(threadId: string, mapping: Record<string, string>) {
  if (typeof window === 'undefined' || !threadId) return;
  try {
    window.localStorage.setItem(`${SHOP_MAPPING_STORAGE_PREFIX}${threadId}`, JSON.stringify(mapping));
  } catch (error) {
    console.warn('Failed to persist merchandising shop mapping', error);
  }
}

interface NewProjectModalProps {
  isOpen: boolean;
  projectType: ProjectType;
  onClose: () => void;
  onProjectCreated: () => void;
  onOpenMerchandisingPlan?: () => void;
  embedded?: boolean;
  onOptimizationConfigured?: () => void | Promise<void>;
  onInputDirtyChange?: () => void;
  formId?: string;
  hideSubmitButton?: boolean;
  onOptimizationReadyChange?: (ready: boolean) => void;
  initialCatalogMode?: CatalogMode | '';
  catalogModeOnly?: boolean;
  onCatalogModeConfirmed?: (mode: CatalogMode, incognito: boolean) => void;
  /** SPJ-1372: mostra il toggle "Ottimizzazione in incognito" (solo admin). */
  isAdmin?: boolean;
  /** SPJ-1372: valore iniziale del flag incognito, propagato all'istanza embedded. */
  initialIncognito?: boolean;
}

export default function NewProjectModal({
  isOpen,
  projectType,
  onClose,
  onProjectCreated,
  onOpenMerchandisingPlan,
  embedded = false,
  onOptimizationConfigured,
  onInputDirtyChange,
  formId,
  hideSubmitButton = false,
  onOptimizationReadyChange,
  initialCatalogMode = '',
  catalogModeOnly = false,
  onCatalogModeConfirmed,
  isAdmin = false,
  initialIncognito = false,
}: NewProjectModalProps) {
  const dispatch = useAppDispatch();
  const thread = useAppSelector((state) => state.thread);

  // SPJ-1372: ottimizzazione in incognito (flag per-progetto nel creation_metadata).
  const [incognito, setIncognito] = useState(initialIncognito);
  
  const [formData, setFormData] = useState({
    negozio: '',
    gender: '',
    season: '',
    consegna: '',
    macrotema: '',
    valueColumn: '',
    merchandisingPlanFile: null as File | null,
    budgetDeliveryFile: null as File | null,
    optimizationTags: [] as string[],
  });
  
  // Shop mode: 'single' for Singolo negozio, 'multi' for Multi-shop
  const [catalogMode, setCatalogMode] = useState<CatalogMode | ''>(initialCatalogMode);
  const [shopMode, setShopMode] = useState<'single' | 'multi'>('single');
  // Store negozio-list from MP upload for multi-shop mode
  const [mpNegozioList, setMpNegozioList] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [optimizationProgress, setOptimizationProgress] = useState(0);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isFinalizingConfig, setIsFinalizingConfig] = useState(false);
  
  // Available options from backend
  const [availableShops, setAvailableShops] = useState<string[]>([]);
  // SPJ-1728: nazione (ISO2) per codice negozio, da list-available-shops.
  const [shopNazione, setShopNazione] = useState<Record<string, string>>({});
  const [availableGenders, setAvailableGenders] = useState<string[]>([]);
  const [availableSeasons, setAvailableSeasons] = useState<string[]>([]);
  const [availableConsegne, setAvailableConsegne] = useState<string[]>([]);
  const [availableMacrotemi, setAvailableMacrotemi] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [availableValueColumns, setAvailableValueColumns] = useState<string[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isLoadingValueColumns, setIsLoadingValueColumns] = useState(false);
  
  // Listino data per shop - key is shop_id, value is the listino info
  type Listino = { listino: string; cod_moneta: string };
  type ListinoInfo = {
    listino_rtl: Listino;
    listino_whs: Listino;
  };
  type AvailableListini = {
    listini_rtl: Listino[];
    listini_whs: Listino[];
  };
  const [listinoPerShop, setListinoPerShop] = useState<Record<string, ListinoInfo>>({});
  const [editedListinoPerShop, setEditedListinoPerShop] = useState<Record<string, ListinoInfo>>({});
  const [availableListini, setAvailableListini] = useState<AvailableListini | null>(null);
  const [isLoadingListino, setIsLoadingListino] = useState(false);
  const [isLoadingAvailableListini, setIsLoadingAvailableListini] = useState(false);
  const [listinoValidated, setListinoValidated] = useState(false);
  const [listinoCurrencyWarning, setListinoCurrencyWarning] = useState<string | null>(null);
  const [isUpdatingListino, setIsUpdatingListino] = useState(false);
  const [listinoError, setListinoError] = useState<string | null>(null);
  // true quando si e' arrivati allo step listini ma per la stagione/collezione non ne esiste
  // nessuno (fetch andata a vuoto / 404): mostra un messaggio esplicito in UI.
  const [listiniNotAvailable, setListiniNotAvailable] = useState(false);

  // Total values (without currency selection)
  const [mpTotal, setMpTotal] = useState<number | null>(null);
  const [budgetTotal, setBudgetTotal] = useState<number | null>(null);
  const [tempThreadId, setTempThreadId] = useState<string | null>(null);
  const [previousThreadId, setPreviousThreadId] = useState<string | null>(null);
  const [mpFileError, setMpFileError] = useState<string | null>(null);
  const [budgetFileError, setBudgetFileError] = useState<string | null>(null);
  const [isUploadingMp, setIsUploadingMp] = useState(false);
  const [isUploadingBudget, setIsUploadingBudget] = useState(false);
  const [mpUploadMessage, setMpUploadMessage] = useState<string | null>(null);
  const [budgetUploadMessage, setBudgetUploadMessage] = useState<string | null>(null);
  
  // Data for tables
  // MP table data is now grouped by cod_negozio for multi-shop mode
  type MpTableRow = { tipo_filato: string; items_total: number; currency_value: number; models_unique: number; sku_count: number; avg_price: number };
  const [mpTableData, setMpTableData] = useState<Record<string, MpTableRow[]> | null>(null);
  // mpCodNegozioListOriginal stores the original flat list from the file (for table lookups)
  const [mpCodNegozioListOriginal, setMpCodNegozioListOriginal] = useState<string[]>([]);
  // mpCodNegozioList stores structured format: [area, [n1,n2], [n3,n4,n5], n6]
  const [mpCodNegozioList, setMpCodNegozioList] = useState<(string | string[])[]>([]);
  const [mpCurrentNegozioIndex, setMpCurrentNegozioIndex] = useState(0);
  // Budget table data is also grouped by cod_negozio for multi-shop mode
  type BudgetTableRow = { sotto_consegna: string; items: number; value: number };
  const [budgetTableData, setBudgetTableData] = useState<Record<string, BudgetTableRow[]> | null>(null);
  const [budgetCodNegozioList, setBudgetCodNegozioList] = useState<string[]>([]);
  const [budgetCurrentNegozioIndex, setBudgetCurrentNegozioIndex] = useState(0);
  type SkuListRow = { sku: string; description?: string; lb?: number; ub?: number; notInPerimeter?: boolean };
  const [skuListData, setSkuListData] = useState<SkuListRow[] | null>(null);
  
  // Modal states for viewing tables
  const [showMpTableModal, setShowMpTableModal] = useState(false);
  const [showBudgetTableModal, setShowBudgetTableModal] = useState(false);
  const [showSkuListModal, setShowSkuListModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  
  // SKU list related states
  const [skuListError, setSkuListError] = useState<string | null>(null);
  const [skuListMessage, setSkuListMessage] = useState<string | null>(null);
  const [isUploadingSkuList, setIsUploadingSkuList] = useState(false);
  const [configCreated, setConfigCreated] = useState(false); // Track if config was created for SKU upload
  const [showSkuTextArea, setShowSkuTextArea] = useState(false);
  const [pastedSkuText, setPastedSkuText] = useState('');
  const [showSkuConfirmation, setShowSkuConfirmation] = useState(false);
  const [isSkuPerimeterOnly, setIsSkuPerimeterOnly] = useState(false);
  // SKU validation states
  const [isValidatingSkus, setIsValidatingSkus] = useState(false);
  const [invalidSkus, setInvalidSkus] = useState<Set<string>>(new Set());
  const [availableSkus, setAvailableSkus] = useState<string[]>([]);
  const [skuValidationDone, setSkuValidationDone] = useState(false);

  // Config popup states
  const [showConfigPopup, setShowConfigPopup] = useState(false);
  const [constraints, setConstraints] = useState<any>(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  
  // Confirmation popup before creating project
  const [showConfigConfirmPopup, setShowConfigConfirmPopup] = useState(false);
  const [tipoFilatoOverrides, setTipoFilatoOverrides] = useState<Array<{
    id: number;
    tipo_filato: string;
    value: number;
  }>>([]);
  const [minAllowedValueTipoFilatoOverrides, setMinAllowedValueTipoFilatoOverrides] = useState<Array<{
    id: number;
    tipo_filato: string;
    value: number;
  }>>([]);
  const [tipoFilatoEnum, setTipoFilatoEnum] = useState<string[]>([]);

  // Shop mapping states
  const [showShopMappingModal, setShowShopMappingModal] = useState(false);
  const [shopMappingOptions, setShopMappingOptions] = useState<Record<string, string>>({});  // key -> display value
  const [shopMappingSelections, setShopMappingSelections] = useState<Record<string, string[]>>({});  // cod_negozio -> selected keys[]
  const [isLoadingShopMapping, setIsLoadingShopMapping] = useState(false);
  const [isUpdatingShopMapping, setIsUpdatingShopMapping] = useState(false);
  const [shopMappingError, setShopMappingError] = useState<string | null>(null);
  const [shopMappingSearchTerm, setShopMappingSearchTerm] = useState('');  // Search term for filtering shops
  const [shopMappingNazione, setShopMappingNazione] = useState('');  // SPJ-1728: filtro nazione nel mapping
  // SPJ-1727/1728: ricerca e filtro nazione nella tabella dei listini.
  const [listiniSearchTerm, setListiniSearchTerm] = useState('');
  const [listiniNazione, setListiniNazione] = useState('');
  const [unparsableNegozi, setUnparsableNegozi] = useState<Set<number>>(new Set());  // Indices of negozi that couldn't be auto-parsed
  const [negoziParsingDone, setNegoziParsingDone] = useState(false);  // Whether negozi parsing has been attempted

  const isOptimizationSubmitDisabled = useMemo(() => {
    if (projectType === 'optimization' && catalogModeOnly) {
      return isCreating || isOptimizing || isFinalizingConfig || !catalogMode;
    }

    return (
      isCreating
      || isOptimizing
      || isFinalizingConfig
      || (projectType === 'optimization' && (!mpTableData || !budgetTableData))
      || (projectType === 'optimization' && Object.keys(listinoPerShop).length > 0 && !listinoValidated)
      // Se non ci sono listini per la stagione/collezione non si puo' proseguire a
      // "configura perimetro e budget": il CTA resta disabilitato.
      || (projectType === 'optimization' && listiniNotAvailable)
    );
  }, [budgetTableData, catalogMode, catalogModeOnly, isCreating, isFinalizingConfig, isOptimizing, listinoPerShop, listinoValidated, listiniNotAvailable, mpTableData, projectType]);

  const isCatalogModeLocked = projectType === 'optimization' && !!tempThreadId;

  useEffect(() => {
    if (projectType !== 'optimization') return;
    onOptimizationReadyChange?.(!isOptimizationSubmitDisabled);
  }, [isOptimizationSubmitDisabled, onOptimizationReadyChange, projectType]);

  useEffect(() => {
    if (isOpen && projectType === 'optimization' && catalogMode && !tempThreadId && !catalogModeOnly) {
      void createTempThreadAndLoadOptions(catalogMode);
    }
  }, [catalogMode, catalogModeOnly, isOpen, projectType, tempThreadId]);

  // Trigger negozi parsing when gender changes and we have a negozio list
  useEffect(() => {
    if (formData.gender && mpCodNegozioListOriginal.length > 0 && !negoziParsingDone && shopMode === 'multi') {
      parseNegoziMapping(mpCodNegozioListOriginal);
    }
  }, [formData.gender, mpCodNegozioListOriginal.length, negoziParsingDone, shopMode]);

  // Load value columns when gender is selected
  useEffect(() => {
    const loadValueColumns = async () => {
      if (!formData.gender || !tempThreadId || !thread.threadData) {
        setAvailableValueColumns([]);
        return;
      }

      setIsLoadingValueColumns(true);
      try {
        const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
        if (!messageId) return;

        const result = await performAction({
          threadId: tempThreadId,
          messageId: messageId,
          actionName: 'get-value-column',
        });

        // Ensure result is an array
        if (Array.isArray(result)) {
          setAvailableValueColumns(result);
        } else if (result && typeof result === 'object') {
          // If result is an object, try to extract array from common properties
          const possibleArray = (result as any).data || (result as any).columns || (result as any).values || Object.values(result);
          setAvailableValueColumns(Array.isArray(possibleArray) ? possibleArray : []);
        } else {
          setAvailableValueColumns([]);
        }
        
        console.log('Value columns result:', result);
      } catch (err) {
        console.error('Error loading value columns:', err);
        setAvailableValueColumns([]);
      } finally {
        setIsLoadingValueColumns(false);
      }
    };

    loadValueColumns();
  }, [formData.gender, tempThreadId, thread.threadData]);

  // Function to fetch listino data for all shops after budget upload is complete
  // Uses lookupId for API call but stores result under compositeId
  const fetchListinoForShopsWithCompositeIds = async (shopIdMappings: { compositeId: string; lookupId: string }[]) => {
    if (!tempThreadId || !thread.threadData || shopIdMappings.length === 0) {
      return;
    }

    setIsLoadingListino(true);
    setListiniNotAvailable(false);
    try {
      const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
      if (!messageId) return;

      const listinoData: Record<string, ListinoInfo> = {};

      // Fetch listino for each shop in parallel
      const results = await Promise.all(
        shopIdMappings.map(async ({ compositeId, lookupId }) => {
          try {
            const result = await performAction({
              threadId: tempThreadId,
              messageId: messageId,
              actionName: 'get-listino-by-shop-id',
              params: { 'shop-id': lookupId,
                'season-id': formData.season
              }
            });
            return { compositeId, data: result as ListinoInfo };
          } catch (err) {
            console.error(`Error fetching listino for shop ${lookupId} (composite: ${compositeId}):`, err);
            return { compositeId, data: null };
          }
        })
      );

      results.forEach(({ compositeId, data }) => {
        if (data) {
          listinoData[compositeId] = data;
        }
      });

      setListinoPerShop(listinoData);
      setEditedListinoPerShop(JSON.parse(JSON.stringify(listinoData))); // Deep copy for editing
      setListinoValidated(false);
      // Se nessun negozio ha restituito un listino (tipicamente 404: stagione/collezione
      // senza listini caricati), segnaliamo l'assenza per mostrare un messaggio in UI.
      setListiniNotAvailable(Object.keys(listinoData).length === 0);

      // After loading shop listini, load available listini
      await loadAvailableListini();
    } catch (err) {
      console.error('Error fetching listino data:', err);
      setListiniNotAvailable(true);
    } finally {
      setIsLoadingListino(false);
    }
  };

  // Function to load all available listini options
  const loadAvailableListini = async () => {
    if (!tempThreadId || !thread.threadData) return;

    setIsLoadingAvailableListini(true);
    try {
      const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
      if (!messageId) return;

      const result = await performAction({
        threadId: tempThreadId,
        messageId: messageId,
        actionName: 'list-available-listini',
        params: {
          'season-id': formData.season
        }
      });

      if (result && typeof result === 'object') {
        const availableResult = result as AvailableListini;
        setAvailableListini({
          listini_rtl: Array.isArray(availableResult.listini_rtl) ? availableResult.listini_rtl : [],
          listini_whs: Array.isArray(availableResult.listini_whs) ? availableResult.listini_whs : [],
        });
      }
    } catch (err) {
      console.error('Error loading available listini:', err);
    } finally {
      setIsLoadingAvailableListini(false);
    }
  };

  // Function to validate currency consistency across all shops and listino types
  const validateListinoCurrency = (editedListini: Record<string, ListinoInfo>): string | null => {
    const currencies = new Set<string>();
    const details: string[] = [];

    Object.entries(editedListini).forEach(([shopId, listino]) => {
      currencies.add(listino.listino_rtl.cod_moneta);
      currencies.add(listino.listino_whs.cod_moneta);
      
      // Check within same shop
      if (listino.listino_rtl.cod_moneta !== listino.listino_whs.cod_moneta) {
        details.push(`${shopId}: RTL (${listino.listino_rtl.cod_moneta}) ≠ WHS (${listino.listino_whs.cod_moneta})`);
      }
    });

    if (currencies.size > 1) {
      const currencyList = Array.from(currencies).join(', ');
      return `Attenzione: Le valute dei listini non sono uniformi (${currencyList}). ${details.length > 0 ? 'Dettagli: ' + details.join('; ') : ''}`;
    }
    
    return null;
  };

  // Function to handle listino selection change
  const handleListinoChange = (shopId: string, type: 'rtl' | 'whs', selectedListino: Listino) => {
    markInputDirty();
    setEditedListinoPerShop(prev => {
      const updated = {
        ...prev,
        [shopId]: {
          ...prev[shopId],
          [type === 'rtl' ? 'listino_rtl' : 'listino_whs']: selectedListino,
        },
      };
      
      // Validate currency after change
      const warning = validateListinoCurrency(updated);
      setListinoCurrencyWarning(warning);
      
      return updated;
    });
    setListinoValidated(false);
  };

  // Function to confirm and save listini configuration
  const handleConfirmListini = async () => {
    if (!tempThreadId || !thread.threadData) return;

    setIsUpdatingListino(true);
    setListinoError(null);
    
    try {
      const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
      if (!messageId) throw new Error('Nessun messaggio disponibile nel thread');

      // Prepare the listini_shop structure for the API
      // Format: { shop_id: { listino_rtl: {...}, listino_whs: {...} }, ... }
      const listiniShop: Record<string, ListinoInfo> = {};
      Object.entries(editedListinoPerShop).forEach(([shopId, listino]) => {
        listiniShop[shopId] = {
          listino_rtl: listino.listino_rtl,
          listino_whs: listino.listino_whs,
        };
      });

      // Create config if not created yet - requires initializing dataset first
      if (!configCreated) {
        // Initialize dataset first
        const initDatasetParams: Record<string, any> = {
          'optim-cod-stagione': formData.season,
          'optim-consegna': formData.consegna,
          'optim-gender': formData.gender,
          'optim-section': formData.macrotema,
        };
        
        // In single shop mode, pass the negozio; in multi-shop, pass the negozio-list
        if (shopMode === 'single') {
          initDatasetParams['optim-cod-negozio'] = formData.negozio;
        } else if (mpCodNegozioList.length > 0) {
          // Use mpCodNegozioList which has the structured format with codes (not display names)
          initDatasetParams['negozio-list'] = mpCodNegozioList;
        }
        
        await performAction({ 
          threadId: tempThreadId, 
          messageId: messageId, 
          actionName: 'optim-form',
          params: { 
            'optim-form-name': 'init-dataset',
            'optim-form-data': initDatasetParams
          }
        });

        // Initialize config (this requires dataset to be created first)
        await performAction({
          threadId: tempThreadId,
          messageId: messageId,
          actionName: 'optim-init-config',
          params: { 'optim-form-data': {} }
        });
        
        setConfigCreated(true);
      }

      await performAction({
        threadId: tempThreadId,
        messageId: messageId,
        actionName: 'update-config-listino',
        params: {
          'optim-listini-shop': listiniShop,
        },
      });

      setListinoValidated(true);
      setListinoPerShop(JSON.parse(JSON.stringify(editedListinoPerShop))); // Update the original with confirmed values
      await loadConfig();
    } catch (err) {
      console.error('Error updating listino config:', err);
      setListinoError(err instanceof Error ? err.message : 'Errore durante l\'aggiornamento dei listini');
    } finally {
      setIsUpdatingListino(false);
    }
  };

  const createTempThreadAndLoadOptions = async (selectedCatalogMode: CatalogMode) => {
    setIsLoadingOptions(true);
    try {
      // Save the current thread ID before creating a new one
      const currentThreadId = thread.threadId;
      setPreviousThreadId(currentThreadId);
      
      // Create a thread for optimization project (this will be the actual project thread)
      const { threadId, thread: threadData } = await dispatch(createNewProject({
        title: 'Nuovo Progetto Ottimizzazione',
        creationMetadata: {
          project_type: 'optimization',
          catalog_mode: selectedCatalogMode,
          ...(incognito ? { incognito: true } : {}),
        }
      })).unwrap();

      // Fetch the thread to get the messages
      // Get the message ID from thread state
      const messageId = threadData.local_memory?.thread_state?.['current-message'] as string;
      
      if (!messageId) {
        throw new Error('Nessun messaggio disponibile nel thread');
      }
      
      setTempThreadId(threadId);
      
      // Load all available options in parallel
      const [shops, genders, seasons, consegne, macrotemi, tags] = await Promise.all([
        performAction({ threadId, messageId, actionName: 'list-available-shops' }),
        performAction({ threadId, messageId, actionName: 'list-available-genders' }),
        performAction({ threadId, messageId, actionName: 'list-available-seasons' }),
        performAction({ threadId, messageId, actionName: 'list-available-consegna' }),
        performAction({ threadId, messageId, actionName: 'list-available-sections' }),
        performAction({ threadId, messageId, actionName: 'list-available-tags' }),
      ]);

      // Extract shops list from response
      const shopsData = (shops as any)?.['global-page-data']?.shops_list || [];
      setAvailableShops(shopsData);
      setShopNazione((shops as any)?.['global-page-data']?.['shops-nazione'] || {});
      setAvailableGenders((genders as any) || []);
      setAvailableSeasons((seasons as any) || []);
      setAvailableConsegne((consegne as any) || []);
      setAvailableMacrotemi((macrotemi as any) || []);
      setAvailableTags((tags as any) || []);
    } catch (err) {
      console.error('Errore nel caricamento delle opzioni:', err);
      setError('Errore nel caricamento delle opzioni disponibili');
    } finally {
      setIsLoadingOptions(false);
    }
  };

  // Handler for changing shop mode
  const markInputDirty = () => {
    onInputDirtyChange?.();
  };

  const handleShopModeChange = (mode: 'single' | 'multi') => {
    markInputDirty();
    setShopMode(mode);
    // Clear all form data when switching modes
    setFormData(prev => ({
      ...prev,
      negozio: '',
      season: '',
      consegna: '',
      macrotema: '',
      gender: '',
      merchandisingPlanFile: null,
      budgetDeliveryFile: null,
      optimizationTags: [],
    }));
    setMpFileError(null);
    setBudgetFileError(null);
    setMpUploadMessage(null);
    setBudgetUploadMessage(null);
    setMpTableData(null);
    setBudgetTableData(null);
    setMpTotal(null);
    setBudgetTotal(null);
    setSkuListData(null);
    setSkuListError(null);
    setSkuListMessage(null);
    setConfigCreated(false);
    setShowSkuTextArea(false);
    setPastedSkuText('');
    setShowSkuConfirmation(false);
    setInvalidSkus(new Set());
    setAvailableSkus([]);
    setSkuValidationDone(false);
    setMpNegozioList([]);
    setMpCodNegozioListOriginal([]);
    setMpCodNegozioList([]);
    setMpCurrentNegozioIndex(0);
    setBudgetCodNegozioList([]);
    setBudgetCurrentNegozioIndex(0);
  };

  // Helper function to clear dependent fields
  const clearDependentFields = (changedField: 'negozio' | 'season' | 'consegna' | 'macrotema' | 'gender' | 'valueColumn') => {
    markInputDirty();
    const updates: Partial<typeof formData> = {};
    
    switch (changedField) {
      case 'negozio':
        // Clear everything after negozio
        updates.season = '';
        updates.consegna = '';
        updates.macrotema = '';
        updates.gender = '';
        updates.valueColumn = '';
        updates.merchandisingPlanFile = null;
        updates.budgetDeliveryFile = null;
        updates.optimizationTags = [];
        setListinoPerShop({});
        setEditedListinoPerShop({});
        setAvailableListini(null);
        setListinoValidated(false);
        setListinoCurrencyWarning(null);
        setListinoError(null);
        setListiniNotAvailable(false);
        setMpFileError(null);
        setBudgetFileError(null);
        setMpUploadMessage(null);
        setBudgetUploadMessage(null);
        setMpTableData(null);
        setBudgetTableData(null);
        setMpTotal(null);
        setBudgetTotal(null);
        setSkuListData(null);
        setSkuListError(null);
        setSkuListMessage(null);
        setConfigCreated(false);
        setShowSkuTextArea(false);
        setPastedSkuText('');
        setShowSkuConfirmation(false);
        setInvalidSkus(new Set());
        setAvailableSkus([]);
        setSkuValidationDone(false);
        setMpNegozioList([]);
        setMpCodNegozioListOriginal([]);
        setMpCodNegozioList([]);
        setMpCurrentNegozioIndex(0);
        setBudgetCodNegozioList([]);
        setBudgetCurrentNegozioIndex(0);
        break;
      
      case 'season':
        // Clear everything after season
        updates.consegna = '';
        updates.macrotema = '';
        updates.gender = '';
        updates.valueColumn = '';
        updates.merchandisingPlanFile = null;
        updates.budgetDeliveryFile = null;
        updates.optimizationTags = [];
        setListinoPerShop({});
        setEditedListinoPerShop({});
        setAvailableListini(null);
        setListinoValidated(false);
        setListinoCurrencyWarning(null);
        setListinoError(null);
        setListiniNotAvailable(false);
        setMpFileError(null);
        setBudgetFileError(null);
        setMpUploadMessage(null);
        setBudgetUploadMessage(null);
        setMpTableData(null);
        setBudgetTableData(null);
        setMpTotal(null);
        setBudgetTotal(null);
        setSkuListData(null);
        setSkuListError(null);
        setSkuListMessage(null);
        setConfigCreated(false);
        setShowSkuTextArea(false);
        setPastedSkuText('');
        setShowSkuConfirmation(false);
        setInvalidSkus(new Set());
        setAvailableSkus([]);
        setSkuValidationDone(false);
        setMpNegozioList([]);
        setMpCodNegozioListOriginal([]);
        setMpCodNegozioList([]);
        setMpCurrentNegozioIndex(0);
        setBudgetCodNegozioList([]);
        setBudgetCurrentNegozioIndex(0);
        break;
      
      case 'consegna':
        // Clear everything after consegna
        updates.macrotema = '';
        updates.gender = '';
        updates.valueColumn = '';
        updates.merchandisingPlanFile = null;
        updates.budgetDeliveryFile = null;
        updates.optimizationTags = [];
        setListinoPerShop({});
        setEditedListinoPerShop({});
        setAvailableListini(null);
        setListinoValidated(false);
        setListinoCurrencyWarning(null);
        setListinoError(null);
        setListiniNotAvailable(false);
        setMpFileError(null);
        setBudgetFileError(null);
        setMpUploadMessage(null);
        setBudgetUploadMessage(null);
        setMpTableData(null);
        setBudgetTableData(null);
        setMpTotal(null);
        setBudgetTotal(null);
        setSkuListData(null);
        setSkuListError(null);
        setSkuListMessage(null);
        setConfigCreated(false);
        setShowSkuTextArea(false);
        setPastedSkuText('');
        setShowSkuConfirmation(false);
        setInvalidSkus(new Set());
        setAvailableSkus([]);
        setSkuValidationDone(false);
        setMpNegozioList([]);
        setMpCodNegozioListOriginal([]);
        setMpCodNegozioList([]);
        setMpCurrentNegozioIndex(0);
        setBudgetCodNegozioList([]);
        setBudgetCurrentNegozioIndex(0);
        break;
      
      case 'macrotema':
        // Clear everything after macrotema
        updates.gender = '';
        updates.valueColumn = '';
        updates.merchandisingPlanFile = null;
        updates.budgetDeliveryFile = null;
        updates.optimizationTags = [];
        setListinoPerShop({});
        setEditedListinoPerShop({});
        setAvailableListini(null);
        setListinoValidated(false);
        setListinoCurrencyWarning(null);
        setListinoError(null);
        setListiniNotAvailable(false);
        setMpFileError(null);
        setBudgetFileError(null);
        setMpUploadMessage(null);
        setBudgetUploadMessage(null);
        setMpTableData(null);
        setBudgetTableData(null);
        setMpTotal(null);
        setBudgetTotal(null);
        setSkuListData(null);
        setSkuListError(null);
        setSkuListMessage(null);
        setConfigCreated(false);
        setShowSkuTextArea(false);
        setPastedSkuText('');
        setShowSkuConfirmation(false);
        setInvalidSkus(new Set());
        setAvailableSkus([]);
        setSkuValidationDone(false);
        setMpNegozioList([]);
        setMpCodNegozioListOriginal([]);
        setMpCodNegozioList([]);
        setMpCurrentNegozioIndex(0);
        setBudgetCodNegozioList([]);
        setBudgetCurrentNegozioIndex(0);
        break;
      
      case 'gender':
        // Clear valueColumn and files that depend on gender
        updates.valueColumn = '';
        updates.merchandisingPlanFile = null;
        updates.budgetDeliveryFile = null;
        updates.optimizationTags = [];
        setMpFileError(null);
        setBudgetFileError(null);
        setMpUploadMessage(null);
        setBudgetUploadMessage(null);
        setMpTableData(null);
        setBudgetTableData(null);
        setMpTotal(null);
        setBudgetTotal(null);
        setSkuListData(null);
        setSkuListError(null);
        setSkuListMessage(null);
        setConfigCreated(false);
        setShowSkuTextArea(false);
        setPastedSkuText('');
        setShowSkuConfirmation(false);
        setInvalidSkus(new Set());
        setAvailableSkus([]);
        setSkuValidationDone(false);
        setMpNegozioList([]);
        setMpCodNegozioListOriginal([]);
        setMpCodNegozioList([]);
        setMpCurrentNegozioIndex(0);
        setBudgetCodNegozioList([]);
        setBudgetCurrentNegozioIndex(0);
        // Reset shop mapping state when gender changes
        setShopMappingOptions({});
        setShopMappingSelections({});
        setUnparsableNegozi(new Set());
        setNegoziParsingDone(false);
        setListinoPerShop({});
        setEditedListinoPerShop({});
        setAvailableListini(null);
        setListinoValidated(false);
        setListinoCurrencyWarning(null);
        setListinoError(null);
        setListiniNotAvailable(false);
        break;
      
      case 'valueColumn':
        // Clear files that depend on valueColumn
        updates.merchandisingPlanFile = null;
        updates.budgetDeliveryFile = null;
        updates.optimizationTags = [];
        setMpFileError(null);
        setBudgetFileError(null);
        setMpUploadMessage(null);
        setBudgetUploadMessage(null);
        setMpTableData(null);
        setBudgetTableData(null);
        setMpTotal(null);
        setBudgetTotal(null);
        setSkuListData(null);
        setSkuListError(null);
        setSkuListMessage(null);
        setConfigCreated(false);
        setShowSkuTextArea(false);
        setPastedSkuText('');
        setShowSkuConfirmation(false);
        setInvalidSkus(new Set());
        setAvailableSkus([]);
        setSkuValidationDone(false);
        setMpNegozioList([]);
        setMpCodNegozioListOriginal([]);
        setMpCodNegozioList([]);
        setMpCurrentNegozioIndex(0);
        setBudgetCodNegozioList([]);
        setBudgetCurrentNegozioIndex(0);
        setListinoPerShop({});
        setEditedListinoPerShop({});
        setAvailableListini(null);
        setListinoValidated(false);
        setListinoCurrencyWarning(null);
        setListinoError(null);
        setListiniNotAvailable(false);
        break;
    }
    
    return updates;
  };

  // Handle value column selection - call action to set it
  const handleValueColumnChange = async (value: string) => {
    if (!tempThreadId || !thread.threadData) return;
    
    setFormData(prev => ({ ...prev, valueColumn: value }));
    
    if (value) {
      try {
        const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
        if (!messageId) return;

        await performAction({
          threadId: tempThreadId,
          messageId: messageId,
          actionName: 'optim-set-value-column',
          params: { 'optim-value-column': value }
        });
      } catch (err) {
        console.error('Error setting value column:', err);
      }
    }
  };

  // Note: Currency conversion functions removed - listino info from backend now provides currency data

  // Helper function to format a shop name from ALL CAPS to Title Case
  const formatShopName = (name: string): string => {
    return name
      .trim()
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper function to parse shop codes that may contain | or |,| separator and map to names
  // SPJ-1726/1728: negozi della selezione singola, "COD - Nome" -> {codice, nome, nazione}.
  const shopPickerOptions = useMemo<ShopOption[]>(() => availableShops.map((shop) => {
    const [code, ...rest] = shop.split(' - ');
    return { code, name: rest.join(' - ') || code, nazione: shopNazione[code] };
  }), [availableShops, shopNazione]);
  // SPJ-1725/1728: opzioni del mapping negozi con codice e nazione.
  const shopMappingOptionList = useMemo<ShopOption[]>(() => Object.entries(shopMappingOptions).map(([code, name]) => (
    { code, name, nazione: shopNazione[code] }
  )), [shopMappingOptions, shopNazione]);

  const getShopDisplayName = (code: string): string => {
    // Check for |,| separator first (more specific)
    if (code.includes('|,|')) {
      // Split by |,| and map each code to its name, then format to title case
      return code.split('|,|')
        .map(c => c.trim())
        .filter(c => c.length > 0)
        .map(c => {
          const name = shopMappingOptions[c] || c;
          return formatShopName(name);
        })
        .join(', ');
    }
    // Check for | separator (from array.join('|'))
    if (code.includes('|,|')) {
      // Split by | and map each code to its name, then format to title case
      return code.split('|,|')
        .map(c => c.trim())
        .filter(c => c.length > 0)
        .map(c => {
          const name = shopMappingOptions[c] || c;
          return formatShopName(name);
        })
        .join(', ');
    }
    const name = shopMappingOptions[code] || code;
    return formatShopName(name);
  };

  const handleFileChange = (field: 'merchandisingPlanFile' | 'budgetDeliveryFile') => async (e: React.ChangeEvent<HTMLInputElement>) => {
    markInputDirty();
    const file = e.target.files?.[0] || null;
    
    // Reset the input value immediately to allow re-uploading the same file
    // This must be done before any async operation to ensure the input is cleared
    e.target.value = '';
    
    setFormData(prev => ({ ...prev, [field]: file }));

    if (!file) {
      if (field === 'merchandisingPlanFile') {
        setMpFileError(null);
        setMpTableData(null);
      } else {
        setBudgetFileError(null);
        setBudgetTableData(null);
      }
      return;
    }

    // Validate and upload file immediately
    if (field === 'merchandisingPlanFile') {
      await handleMpFileUpload(file);
    } else {
      await handleBudgetFileUpload(file);
    }
  };

  const handleMpFileUpload = async (file: File) => {
    // Validate file format
    if (!file.name.match(/\.(xlsx?|csv)$/i)) {
      setMpFileError('Il file deve essere in formato Excel o CSV');
      setFormData(prev => ({ ...prev, merchandisingPlanFile: null }));
      return;
    }
    setMpFileError(null);
    setMpUploadMessage(null);

    // Check if we have required data for upload
    if (!tempThreadId || !thread.threadData) {
      return;
    }

    if (!formData.gender || !formData.consegna || !formData.valueColumn) {
      setMpFileError('Seleziona prima Gender, Consegna e Colonna Valore prima di caricare il file MP');
      setFormData(prev => ({ ...prev, merchandisingPlanFile: null }));
      return;
    }

    // Reset listino states when MP is re-uploaded
    setListinoPerShop({});
    setEditedListinoPerShop({});
    setAvailableListini(null);
    setListinoValidated(false);
    setListinoCurrencyWarning(null);
    setListinoError(null);
    
    // Also reset budget-related data since MP change invalidates it
    setBudgetTableData(null);
    setBudgetTotal(null);
    setBudgetFileError(null);
    setBudgetUploadMessage(null);
    setFormData(prev => ({ ...prev, budgetDeliveryFile: null }));

    setIsUploadingMp(true);
    try {
      const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
      if (!messageId) {
        throw new Error('Nessun messaggio disponibile nel thread');
      }

      // First, set gender and consegna via actions
      await performAction({ 
        threadId: tempThreadId, 
        messageId: messageId, 
        actionName: 'optim-set-gender',
        params: { 'optim-gender': formData.gender }
      });

      await performAction({ 
        threadId: tempThreadId, 
        messageId: messageId, 
        actionName: 'optim-set-consegna',
        params: { 'optim-consegna': formData.consegna }
      });

      if (shopMode === 'single') {
        await performAction({ 
          threadId: tempThreadId, 
          messageId: messageId, 
          actionName: 'optim-set-cod-negozio',
          params: { 'optim-cod-negozio': formData.negozio }
        });
      }

      // Convert file to base64
      const mpFileContent = await fileToBase64(file);
      
      // Upload file via backend persona

      const result = await performAction({ 
        threadId: tempThreadId, 
        messageId: messageId, 
        actionName: 'optim-form',
        params: { 
          'optim-form-name': shopMode === 'multi' ? 'upload-mp-multi' : 'upload-mp',
          'optim-form-data': mpFileContent
        }
      });


      // Extract message and data from result
      const uploadMessage = (result as any)?.message || 'File caricato con successo';
      if ((result as any)?.success === false) {
        setMpFileError((result as any)?.message || 'Errore durante l\'upload del file MP');
        setFormData(prev => ({ ...prev, merchandisingPlanFile: null }));
        setMpTableData(null);
      }
      else {
      setMpUploadMessage(uploadMessage);
      }
      // Extract cod_negozio_list from response
      const codNegozioList: string[] = (result as any)?.cod_negozio_list || (result as any)?.['cod-negozio-list'] || [];
      setMpCodNegozioListOriginal(codNegozioList);
      setMpCodNegozioList(codNegozioList);
      setMpCurrentNegozioIndex(0);
      
      // Extract table data
      // data is a list where each element at index i corresponds to cod_negozio_list[i]
      // Each element has tipo_filato as an object/dict with tipo_filato names as keys
      const data = (result as any)?.data;
      if (data && Array.isArray(data)) {
        const groupedData: Record<string, { tipo_filato: string; items_total: number; currency_value: number; models_unique: number; sku_count: number; avg_price: number }[]> = {};
        
        if (codNegozioList.length > 0) {
          // Process each negozio's data
          codNegozioList.forEach((codNegozio, index) => {
            const negozioData = data[index];
            if (negozioData && negozioData.tipo_filato) {
              // tipo_filato is an object with tipo names as keys
              groupedData[codNegozio] = Object.entries(negozioData.tipo_filato).map(([tipo, values]: [string, any]) => ({
                tipo_filato: tipo,
                items_total: values?.items_total || values?.['items-total'] || 0,
                currency_value: values?.currency_value || values?.['currency-value'] || 0,
                models_unique: values?.models_unique || values?.['models-unique'] || 0,
                sku_count: values?.sku_count || values?.['sku-count'] || 0,
                avg_price: values?.avg_price || values?.['avg-price'] || 0
              }));
            } else {
              groupedData[codNegozio] = [];
            }
          });
        } else if (data.length > 0) {
          // Fallback: single shop mode with just one element in data array
          const singleData = data[0];
          if (singleData && singleData.tipo_filato) {
            groupedData['default'] = Object.entries(singleData.tipo_filato).map(([tipo, values]: [string, any]) => ({
              tipo_filato: tipo,
              items_total: values?.items_total || values?.['items-total'] || 0,
              currency_value: values?.currency_value || values?.['currency-value'] || 0,
              models_unique: values?.models_unique || values?.['models-unique'] || 0,
              sku_count: values?.sku_count || values?.['sku-count'] || 0,
              avg_price: values?.avg_price || values?.['avg-price'] || 0
            }));
            setMpCodNegozioListOriginal(['default']);
            setMpCodNegozioList(['default']);
          }
        }
        
        setMpTableData(groupedData);
        
        // Calculate total MP value across all negozi, excluding the first one (Area Negozio)
        let total = 0;
        const keysToSum = codNegozioList.length > 1 
          ? codNegozioList.slice(1)  // Skip first element (Area Negozio)
          : codNegozioList;  // If only one, use it (fallback for single shop mode)
        keysToSum.forEach(codNegozio => {
          const rows = groupedData[codNegozio];
          if (rows) {
            rows.forEach(row => {
              total += row.currency_value;
            });
          }
        });
        setMpTotal(total);
      }
      
      // Extract negozio-list from response (for multi-shop mode display)
      const negozioList = (result as any)?.negozio_list || (result as any)?.['negozio-list'] || codNegozioList;
      if (negozioList.length > 0) {
        setMpNegozioList(negozioList);
      }
      
      // Clear budget file when MP is uploaded/changed - budget must be re-uploaded
      setFormData(prev => ({ ...prev, budgetDeliveryFile: null }));
      setBudgetTableData(null);
      setBudgetCodNegozioList([]);
      setBudgetCurrentNegozioIndex(0);
      setBudgetTotal(null);
      setBudgetFileError(null);
      setBudgetUploadMessage(null);
      
      // If gender is already set, try to parse the negozi mapping
      if (formData.gender && codNegozioList.length > 0) {
        // Use setTimeout to allow state updates to complete first
        setTimeout(() => {
          parseNegoziMapping(codNegozioList);
        }, 100);
      }
    } catch (err) {
      console.error('Errore durante l\'upload del file MP:', err);
      setMpFileError(err instanceof Error ? err.message : 'Errore durante l\'upload del file');
      setFormData(prev => ({ ...prev, merchandisingPlanFile: null }));
      setMpTableData(null);
      setMpNegozioList([]);
      setMpCodNegozioListOriginal([]);
      setMpCodNegozioList([]);
      setMpCurrentNegozioIndex(0);
    } finally {
      setIsUploadingMp(false);
    }
  };

  const handleBudgetFileUpload = async (file: File) => {
    // Validate file format
    if (!file.name.match(/\.(xlsx?|csv)$/i)) {
      setBudgetFileError('Il file deve essere in formato Excel o CSV');
      setFormData(prev => ({ ...prev, budgetDeliveryFile: null }));
      return;
    }
    setBudgetFileError(null);
    setBudgetUploadMessage(null);

    // Check if we have required data for upload
    if (!tempThreadId || !thread.threadData) {
      return;
    }

    // In multi-shop mode, require MP file to be uploaded first (to get negozio-list)
    if (shopMode === 'multi') {
      if (!formData.merchandisingPlanFile || !mpTableData) {
        setBudgetFileError('Carica prima il file Merchandising Plan per ottenere la lista dei negozi');
        setFormData(prev => ({ ...prev, budgetDeliveryFile: null }));
        return;
      }
      if (!formData.macrotema || !formData.gender || !formData.consegna) {
        setBudgetFileError('Seleziona prima Gender, Consegna e Macrotema prima di caricare il file Budget');
        setFormData(prev => ({ ...prev, budgetDeliveryFile: null }));
        return;
      }
      // Check if all negozi have been properly assigned
      if (unparsableNegozi.size > 0) {
        setBudgetFileError('Assegna tutti i negozi non riconosciuti prima di caricare il file Budget. Clicca su "Modifica Mapping" per assegnare i negozi.');
        setFormData(prev => ({ ...prev, budgetDeliveryFile: null }));
        return;
      }
    } else {
      // Single shop mode
      if (!formData.macrotema || !formData.gender || !formData.negozio || !formData.consegna) {
        setBudgetFileError('Seleziona prima Negozio, Gender, Consegna e Macrotema prima di caricare il file Budget');
        setFormData(prev => ({ ...prev, budgetDeliveryFile: null }));
        return;
      }
    }

    // Reset listino states when Budget is re-uploaded
    setListinoPerShop({});
    setEditedListinoPerShop({});
    setAvailableListini(null);
    setListinoValidated(false);
    setListinoCurrencyWarning(null);
    setListinoError(null);

    setIsUploadingBudget(true);
    try {
      const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
      if (!messageId) {
        throw new Error('Nessun messaggio disponibile nel thread');
      }

      // First, set all required fields via actions
      await performAction({ 
        threadId: tempThreadId, 
        messageId: messageId, 
        actionName: 'optim-set-section',
        params: { 'optim-section': formData.macrotema }
      });

      await performAction({ 
        threadId: tempThreadId, 
        messageId: messageId, 
        actionName: 'optim-set-gender',
        params: { 'optim-gender': formData.gender }
      });
      

      // In single shop mode, set the single negozio
      // In multi-shop mode, we'll pass the negozio-list to the upload action
      if (shopMode === 'single') {
        await performAction({ 
          threadId: tempThreadId, 
          messageId: messageId, 
          actionName: 'optim-set-cod-negozio',
          params: { 'optim-cod-negozio': formData.negozio }
        });
      }

      await performAction({ 
        threadId: tempThreadId, 
        messageId: messageId, 
        actionName: 'optim-set-consegna',
        params: { 'optim-consegna': formData.consegna }
      });

      // Convert file to base64
      const budgetFileContent = await fileToBase64(file);
      
      // Upload file via backend persona
      // In multi-shop mode, pass the negozio-list from the MP upload
      const uploadParams: Record<string, any> = { 
        'optim-form-name': 'upload-budget',
        'optim-form-data': budgetFileContent
      };
      
      if (shopMode === 'multi' && mpCodNegozioList.length > 0) {
        // Exclude the first element (area negozio) from the list
        // Use mpCodNegozioList which has the structured format with codes (not display names)
        uploadParams['negozio-list'] = mpCodNegozioList.slice(1);
      }
      
      const result = await performAction({ 
        threadId: tempThreadId, 
        messageId: messageId, 
        actionName: 'optim-form',
        params: uploadParams
      });

      // Extract message and data from result
      const uploadMessage = (result as any)?.message || 'File caricato con successo';

      if ((result as any)?.success === false) {
        setBudgetFileError((result as any)?.message || 'Errore durante l\'upload del file Budget');
        setFormData(prev => ({ ...prev, budgetDeliveryFile: null }));
        setBudgetTableData(null);
      }
      else {
        setBudgetUploadMessage(uploadMessage);
      }

      // Extract table data
      // Expected schema:
      // - data: List[List[DataForBudget]]
      // - cod_negozio_list: List[str] (same order as data, when present)
      const data = (result as any)?.data;
      const codNegozioList: string[] = (result as any)?.cod_negozio_list || (result as any)?.['cod-negozio-list'] || [];
      
      if (data && Array.isArray(data) && data.length > 0) {
        const groupedData: Record<string, BudgetTableRow[]> = {};
        const resolvedShopKeys: string[] = [];

        data.forEach((negozioData: any, index: number) => {
          const rowList = Array.isArray(negozioData) ? negozioData : [];
          const shopKey =
            codNegozioList[index] ||
            (data.length === 1 ? 'default' : `shop_${index + 1}`);

          resolvedShopKeys.push(shopKey);
          groupedData[shopKey] = rowList.map((item: any) => ({
            sotto_consegna: item.sotto_consegna,
            items: Number(item.items ?? 0),
            value: Number(item.value ?? 0)
          }));
        });

        setBudgetTableData(groupedData);
        setBudgetCodNegozioList(resolvedShopKeys);
        setBudgetCurrentNegozioIndex(0);
        
        // Calculate total budget value across all negozi
        let total = 0;
        Object.values(groupedData).forEach(rows => {
          rows.forEach(row => {
            total += row.value;
          });
        });
        setBudgetTotal(total);

        // Fetch listino data for all shops (not area)
        // In multi-shop mode, get shop IDs from mpCodNegozioList (skip first element which is area)
        // In single-shop mode, use formData.negozio
        // For composite shop IDs (arrays or containing |,|), only use the first cod negozio for listino lookup
        // but store with the composite key (array joined with |)
        const shopIdsToFetch: { compositeId: string; lookupId: string }[] = [];
        if (shopMode === 'multi' && mpCodNegozioList.length > 1) {
          // Extract shop IDs from mpCodNegozioList, skipping first element (area)
          mpCodNegozioList.slice(1).forEach(entry => {
            if (Array.isArray(entry)) {
              // Join array elements with | to create composite ID
              const compositeId = entry.join('|,|');
              // Use only the first element for listino lookup
              const firstElement = entry[0];
              const lookupId = firstElement.includes('|,|') ? firstElement.split('|,|')[0].trim() : firstElement;
              shopIdsToFetch.push({ compositeId, lookupId });
            } else if (typeof entry === 'string') {
              // For string entries, composite ID is the same as the entry
              const compositeId = entry;
              // For composite shop IDs (containing |,|), use only the first part for listino lookup
              const lookupId = entry.includes('|,|') ? entry.split('|,|')[0].trim() : entry;
              shopIdsToFetch.push({ compositeId, lookupId });
            }
          });
        } else if (shopMode === 'single' && formData.negozio) {
          const compositeId = formData.negozio;
          // For composite shop IDs, use only the first part for listino lookup
          const lookupId = formData.negozio.includes('|,|') ? formData.negozio.split('|,|')[0].trim() : formData.negozio;
          shopIdsToFetch.push({ compositeId, lookupId });
        }

        if (shopIdsToFetch.length > 0) {
          // Fetch listino in background (don't block UI)
          fetchListinoForShopsWithCompositeIds(shopIdsToFetch);
        }
      }
    } catch (err) {
      console.error('Errore durante l\'upload del file Budget:', err);
      setBudgetFileError(err instanceof Error ? err.message : 'Errore durante l\'upload del file');
      setFormData(prev => ({ ...prev, budgetDeliveryFile: null }));
      setBudgetTableData(null);
    } finally {
      setIsUploadingBudget(false);
    }
  };

  const handleTagToggle = (tag: string) => {
    markInputDirty();
    setFormData(prev => ({
      ...prev,
      optimizationTags: prev.optimizationTags.includes(tag)
        ? prev.optimizationTags.filter(t => t !== tag)
        : [...prev.optimizationTags, tag]
    }));
  };

  // Helper function to parse pasted SKU text
  const parseSkuText = (text: string): string[] => {
    if (!text || !text.trim()) {
      return [];
    }
    
    // Split by newlines, commas, or spaces, then filter and trim
    const skus = text
      .split(/[\n,\s]+/)
      .map(sku => sku.trim())
      .filter(sku => sku.length > 0);
    
    return skus;
  };

  const normalizeSkuRows = (raw: any): SkuListRow[] => {
    if (!Array.isArray(raw)) return [];
    const rows: SkuListRow[] = raw
      .map((item: any) => {
        if (typeof item === 'string') return { sku: item };
        const sku = item?.sku || item?.name || item?.SKU;
        if (!sku) return null;
        return {
          sku: String(sku),
          description: item?.description ? String(item.description) : undefined,
          lb: item?.lb_quantity ?? item?.lb ?? undefined,
          ub: item?.ub_quantity ?? item?.ub ?? undefined,
        };
      })
      .filter((row: SkuListRow | null): row is SkuListRow => !!row);
    return rows;
  };

  const extractSkuRowsFromConstraints = (configLike: any): SkuListRow[] => {
    const lbConstraints = configLike?.constraints?.lb_quantity || {};
    const ubConstraints = configLike?.constraints?.ub_quantity || {};
    const allSkus = new Set<string>([
      ...Object.keys(lbConstraints),
      ...Object.keys(ubConstraints),
    ]);
    return Array.from(allSkus).map((sku) => ({
      sku,
      lb: lbConstraints[sku],
      ub: ubConstraints[sku],
    }));
  };

  const handleShowSkuListModal = async () => {
    let latestConfig: any = constraints;
    if (configCreated) {
      const loadedConfig = await loadConfig();
      if (loadedConfig) {
        latestConfig = loadedConfig;
      }
    }

    const configRows = extractSkuRowsFromConstraints(latestConfig);
    if (configRows.length > 0) {
      const descriptionBySku = new Map((skuListData || []).map((row) => [row.sku, row.description]));
      const notInPerimeterBySku = new Map((skuListData || []).map((row) => [row.sku, row.notInPerimeter]));
      setSkuListData(
        configRows.map((row) => ({
          ...row,
          description: descriptionBySku.get(row.sku),
          notInPerimeter: notInPerimeterBySku.get(row.sku) || false,
        }))
      );
    }

    setShowSkuListModal(true);
  };

  // Validate SKUs against available SKUs from backend
  const validateSkus = async (skus: string[]): Promise<{ valid: string[]; invalid: string[] }> => {
    if (!tempThreadId || !thread.threadData) {
      throw new Error('Thread non disponibile');
    }

    const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
    if (!messageId) {
      throw new Error('Nessun messaggio disponibile nel thread');
    }

    try {
      // Call optim-check-available-skus to get available SKUs for the selected consegna/gender/season
      const result = await performAction({
        threadId: tempThreadId,
        messageId: messageId,
        actionName: 'optim-check-available-skus',
        params: {
          'optim-consegna': formData.consegna,
          'optim-gender': formData.gender,
          'optim-season': formData.season,
          'optim-macrotema': formData.macrotema,
        }
      });

      // Extract available SKUs from the response
      const availableSkusList: string[] = (result as any)?.skus || [];
      setAvailableSkus(availableSkusList);

      // Create a Set for faster lookup (case-insensitive)
      const availableSkuSet = new Set(availableSkusList.map(sku => sku.toUpperCase()));

      // Separate valid and invalid SKUs
      const validSkus: string[] = [];
      const invalidSkusList: string[] = [];

      skus.forEach(sku => {
        if (availableSkuSet.has(sku.toUpperCase())) {
          validSkus.push(sku);
        } else {
          invalidSkusList.push(sku);
        }
      });

      return { valid: validSkus, invalid: invalidSkusList };
    } catch (err) {
      console.error('Error validating SKUs:', err);
      throw err;
    }
  };

  // Handle validation before showing confirmation
  const handleValidateAndShowConfirmation = async () => {
    const skus = parseSkuText(pastedSkuText);
    if (skus.length === 0) {
      setSkuListError('Nessuno SKU valido trovato nel testo inserito');
      return;
    }

    // Reset previous validation state
    setInvalidSkus(new Set());
    setSkuValidationDone(false);
    setIsValidatingSkus(true);
    setSkuListError(null);

    try {
      const { valid, invalid } = await validateSkus(skus);
      
      setInvalidSkus(new Set(invalid));
      setSkuValidationDone(true);
      setShowSkuConfirmation(true);
    } catch (err) {
      console.error('Errore durante la validazione degli SKU:', err);
      setSkuListError(err instanceof Error ? err.message : 'Errore durante la validazione degli SKU');
    } finally {
      setIsValidatingSkus(false);
    }
  };

  const handleAddProducts = async (skus: string[], constraintsData?: Record<string, { lb?: number; ub?: number }>) => {
    // Called when products are confirmed
    setShowSkuConfirmation(false);
    
    if (!skus || skus.length === 0) {
      return;
    }

    // Check if we have required data for upload
    if (!tempThreadId || !thread.threadData) {
      setSkuListError('Thread non disponibile');
      return;
    }

    // Check if we have the required optimization dataset initialized
    // SKU list can only be added after the dataset is initialized
    if (!formData.merchandisingPlanFile || !formData.budgetDeliveryFile) {
      setSkuListError('Carica prima i file MP e Budget prima di aggiungere SKU');
      return;
    }

    // Verify all required fields are filled
    if (shopMode === 'single') {
      if (!formData.negozio || !formData.gender || !formData.season || !formData.consegna) {
        setSkuListError('Completa tutti i campi richiesti prima di aggiungere SKU');
        return;
      }
    } else {
      // Multi-shop mode - negozio is not required
      if (!formData.gender || !formData.season || !formData.consegna) {
        setSkuListError('Completa tutti i campi richiesti prima di aggiungere SKU');
        return;
      }
    }

    setIsUploadingSkuList(true);
    setSkuListError(null);
    setSkuListMessage(null);

    try {
      const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
      if (!messageId) {
        throw new Error('Nessun messaggio disponibile nel thread');
      }

      // If config hasn't been created yet, create dataset and config first
      if (!configCreated) {
        // Initialize dataset
        const initDatasetParams: Record<string, any> = {
          'optim-cod-stagione': formData.season,
          'optim-consegna': formData.consegna,
          'optim-gender': formData.gender,
        };
        
        // In single shop mode, pass the negozio; in multi-shop, pass the negozio-list
        if (shopMode === 'single') {
          initDatasetParams['optim-cod-negozio'] = formData.negozio;
        } else if (mpCodNegozioList.length > 0) {
          // Use mpCodNegozioList which has the structured format with codes (not display names)
          initDatasetParams['negozio-list'] = mpCodNegozioList;
        }
        
        await performAction({ 
          threadId: tempThreadId, 
          messageId: messageId, 
          actionName: 'optim-form',
          params: { 
            'optim-form-name': 'init-dataset',
            'optim-form-data': initDatasetParams
          }
        });

        // Initialize config (this requires dataset to be created first)
        await performAction({ 
          threadId: tempThreadId, 
          messageId: messageId, 
          actionName: 'optim-init-config',
          params: { 
            'optim-form-data': {}
          }
        });

        setConfigCreated(true);
        
        // Load the config after initialization so user can modify it
        await loadConfig();
      }

      // Upload SKU list via backend persona
      const lbValues = constraintsData
        ? skus.map((sku) => constraintsData[sku]?.lb ?? (isSkuPerimeterOnly ? 0 : 1))
        : (isSkuPerimeterOnly ? 0 : 1);

      const result = await performAction({ 
        threadId: tempThreadId, 
        messageId: messageId, 
        actionName: 'optim-upload-skus-list',
        params: { 
          'optim-skus-list': skus,
          'optim-lb-quantity': lbValues,
          ...(shopMode === 'single' && formData.negozio ? { 'optim-shop-id': formData.negozio } : {})
        }
      });

      // Extract message and parsed SKU list from result
      const uploadMessage = (result as any)?.message || `${skus.length} SKU aggiunti con successo`;
      
      if ((result as any)?.success === false) {
        setSkuListError((result as any)?.message || 'Errore durante l\'upload degli SKU');
        setSkuListData(null);
      } else {
        const shouldTagOutOfPerimeter = skuValidationDone && invalidSkus.size > 0;
        const notInPerimeterSkuSet = shouldTagOutOfPerimeter
          ? new Set(Array.from(invalidSkus).map((sku) => sku.toUpperCase()))
          : new Set<string>();
        const notInPerimeterCount = skus.filter((sku) => notInPerimeterSkuSet.has(sku.toUpperCase())).length;

        setSkuListMessage(
          notInPerimeterCount > 0
            ? `${uploadMessage} (${notInPerimeterCount} SKU fuori perimetro inclusi)`
            : uploadMessage
        );
        
        // Extract parsed SKU list data
        // The backend returns the parsed list of SKUs
        const parsedSkus = (result as any)?.skus || skus.map(sku => ({ sku }));
        const normalizedRows = normalizeSkuRows(parsedSkus);
        setSkuListData(
          normalizedRows.map((row) => ({
            ...row,
            notInPerimeter: notInPerimeterSkuSet.has(row.sku.toUpperCase()),
          }))
        );
        
        // Update constraints with the config returned from SKU upload
        // This prevents "Configura" from overwriting the updated config
        setConstraints(result as any);
        
        // Close text area and reset
        setShowSkuTextArea(false);
        setPastedSkuText('');
      }
    } catch (err) {
      console.error('Errore durante l\'upload degli SKU:', err);
      setSkuListError(err instanceof Error ? err.message : 'Errore durante l\'upload degli SKU');
      setSkuListData(null);
    } finally {
      setIsUploadingSkuList(false);
    }
  };

  const handleConfirmSkuUpload = async () => {
    const skus = parseSkuText(pastedSkuText);
    if (skus.length === 0) {
      setSkuListError('Nessuno SKU valido trovato nel testo inserito');
      setShowSkuConfirmation(false);
      return;
    }
    
    await handleAddProducts(skus);
  };

  const handleCancelSkuUpload = () => {
    setShowSkuConfirmation(false);
    setShowSkuTextArea(false);
    setPastedSkuText('');
    setSkuListError(null);
    setIsSkuPerimeterOnly(false);
    // Reset validation state
    setInvalidSkus(new Set());
    setAvailableSkus([]);
    setSkuValidationDone(false);
  };

  // Helper function to update constraints with tipo filato overrides
  const updateConstraintsWithOverrides = async (overrides: Array<{ id: number; tipo_filato: string; value: number }>) => {
    if (!constraints) return;
    
    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
    if (!updatedConstraints.preparation) {
      updatedConstraints.preparation = {};
    }
    
    // Build expected_sellout_by_tipo_filato from overrides
    const selloutByTipoFilato: Record<string, number> = {};
    overrides.forEach(o => {
      if (o.tipo_filato) {
        selloutByTipoFilato[o.tipo_filato] = o.value;
      }
    });
    updatedConstraints.preparation.expected_sellout_by_tipo_filato = selloutByTipoFilato;
    
    setConstraints(updatedConstraints);
    
    // Update config on backend
    try {
      const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string;
      if (tempThreadId && messageId) {
        await performAction({
          threadId: tempThreadId,
          messageId,
          actionName: "optim-update-config",
          params: { "optim-config-data": updatedConstraints },
        });
      }
    } catch (err) {
      console.error("Error updating tipo filato overrides:", err);
    }
  };

  const updateMinAllowedValuePerTipoFilatoOverrides = async (overrides: Array<{ id: number; tipo_filato: string; value: number }>) => {
    if (!constraints) return;

    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
    if (!updatedConstraints.constraints) {
      updatedConstraints.constraints = {};
    }

    const minAllowedValuePerTipoFilato: Record<string, number> = {};
    overrides.forEach((override) => {
      if (override.tipo_filato) {
        minAllowedValuePerTipoFilato[override.tipo_filato] = override.value;
      }
    });
    updatedConstraints.constraints.min_allowed_value_per_tipo_filato = minAllowedValuePerTipoFilato;

    setConstraints(updatedConstraints);

    try {
      const messageId = thread.threadData?.local_memory?.thread_state?.['current-message'] as string;
      if (tempThreadId && messageId) {
        await performAction({
          threadId: tempThreadId,
          messageId,
          actionName: 'optim-update-config',
          params: { 'optim-config-data': updatedConstraints },
        });
      }
    } catch (err) {
      console.error('Error updating min_allowed_value_per_tipo_filato overrides:', err);
    }
  };

  // Load config from backend
  const loadConfig = async () => {
    if (!tempThreadId || !thread.threadData) return null;
    
    setIsLoadingConfig(true);
    try {
      const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
      if (!messageId) return;

      // init config if not created yet retrieve config_id
      if (!configCreated) {
        const configResult = await performAction({
          threadId: tempThreadId,
          messageId,
          actionName: "optim-get-config-details",
          params: {},
        });
        setConstraints(configResult);
      }
      
      const configResult = await performAction({
        threadId: tempThreadId,
        messageId,
        actionName: "optim-get-config-details",
        params: {},
      });
      
      console.log("=== CONFIG RESULT (NewProjectModal) ===");
      console.log("Full configResult:", configResult);
      
      setConstraints(configResult);
      
      // Extract tipo filato enum from constraints if available
      const tipoFilatoList = (configResult as any)?.constraints?.tipo_filato_enum || [];
      setTipoFilatoEnum(tipoFilatoList);
      
      // Load existing overrides if any
      const existingOverrides = (configResult as any)?.preparation?.expected_sellout_by_tipo_filato || {};
      const overridesList = Object.entries(existingOverrides).map(([tipo, value], idx) => ({
        id: idx + 1,
        tipo_filato: tipo,
        value: value as number,
      }));
      setTipoFilatoOverrides(overridesList);

      const existingMinAllowedValueOverrides = (configResult as any)?.constraints?.min_allowed_value_per_tipo_filato || {};
      const minAllowedValueOverridesList = Object.entries(existingMinAllowedValueOverrides).map(([tipo, value], idx) => ({
        id: idx + 1,
        tipo_filato: tipo,
        value: typeof value === 'number' ? value : 0,
      }));
      setMinAllowedValueTipoFilatoOverrides(minAllowedValueOverridesList);
      return configResult;
    } catch (err) {
      console.error("Error loading config:", err);
      return null;
    } finally {
      setIsLoadingConfig(false);
    }
  };

  // Handle config button click - creates config if needed, then shows popup
  const handleConfigClick = async () => {
    if (!tempThreadId || !thread.threadData) {
      setError('Thread non disponibile');
      return;
    }
    
    const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
    if (!messageId) {
      setError('Nessun messaggio disponibile nel thread');
      return;
    }
    
    // If config not created yet, create it first
    if (!configCreated) {
      setIsLoadingConfig(true);
      try {
        // Initialize dataset first
        const initDatasetParams: Record<string, any> = {
          'optim-cod-stagione': formData.season,
          'optim-consegna': formData.consegna,
          'optim-gender': formData.gender,
        };
        
        if (shopMode === 'single') {
          initDatasetParams['optim-cod-negozio'] = formData.negozio;
        } else if (mpCodNegozioList.length > 0) {
          // Use mpCodNegozioList which has the structured format with codes (not display names)
          initDatasetParams['negozio-list'] = mpCodNegozioList;
        }
        
        await performAction({ 
          threadId: tempThreadId, 
          messageId: messageId, 
          actionName: 'optim-form',
          params: { 
            'optim-form-name': 'init-dataset',
            'optim-form-data': initDatasetParams
          }
        });

        // Initialize config
        await performAction({ 
          threadId: tempThreadId, 
          messageId: messageId, 
          actionName: 'optim-init-config',
          params: { 
            'optim-form-data': {}
          }
        });

        setConfigCreated(true);
        await loadConfig();
      } catch (err) {
        console.error("Error creating config:", err);
        setError(err instanceof Error ? err.message : 'Errore durante la creazione della configurazione');
        setIsLoadingConfig(false);
        return;
      }
    } else if (!constraints) {
      // Config was created but not loaded yet
      await loadConfig();
    }
    
    setShowConfigPopup(true);
  };

  // Parse negozi after MP upload - tries to match cod_negozio with shop mapping options
  const parseNegoziMapping = async (codNegozioList: string[]) => {
    if (!tempThreadId || !thread.threadData || !formData.gender) {
      return;
    }

    setIsLoadingShopMapping(true);
    
    try {
      const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
      
      if (!messageId) {
        return;
      }

      // Call optim-get-shop-mapping with optim-gender param
      const result = await performAction({
        threadId: tempThreadId,
        messageId: messageId,
        actionName: 'optim-get-shop-mapping',
        params: { 'optim-gender': formData.gender }
      });

      // Result is a dict where keys are backend identifiers and values are display choices
      const mappingOptions = (result as Record<string, string>) || {};
      setShopMappingOptions(mappingOptions);

      // Try to auto-parse each cod_negozio (except the first one which is area negozio)
      const unparsable = new Set<number>();
      const parsedList: (string | string[])[] = [];
      const initialSelections: Record<string, string[]> = {};

      codNegozioList.forEach((codNegozio, index) => {
        const selectionKey = `entry_${index}`;
        
        if (index === 0) {
          // First one is area negozio - don't parse, keep as-is
          parsedList.push(codNegozio);
          initialSelections[selectionKey] = [codNegozio];
        } else {
          // Try to find a matching key in the mapping options
          const matchingKey = Object.keys(mappingOptions).find(key => 
            mappingOptions[key] === codNegozio || 
            key === codNegozio 
            // ||
            // mappingOptions[key].toLowerCase().includes(codNegozio.toLowerCase()) ||
            // codNegozio.toLowerCase().includes(mappingOptions[key].toLowerCase())
          );
          
          if (matchingKey) {
            // Found a match - use the key
            parsedList.push(matchingKey);
            initialSelections[selectionKey] = [matchingKey];
          } else {
            // No match found - mark as unparsable
            unparsable.add(index);
            parsedList.push(codNegozio);
            initialSelections[selectionKey] = [];  // Empty selection - user must assign
          }
        }
      });

      setUnparsableNegozi(unparsable);
      setMpCodNegozioList(parsedList);
      setShopMappingSelections(initialSelections);
      setNegoziParsingDone(true);
      
    } catch (err) {
      console.error('Error parsing negozi mapping:', err);
      // On error, mark all (except first) as unparsable
      const unparsable = new Set<number>();
      codNegozioList.forEach((_, index) => {
        if (index > 0) unparsable.add(index);
      });
      setUnparsableNegozi(unparsable);
      setNegoziParsingDone(true);
    } finally {
      setIsLoadingShopMapping(false);
    }
  };

  // Handle shop mapping button click - loads shop mapping options and shows modal
  const handleShopMappingClick = async () => {
    if (!tempThreadId || !thread.threadData) {
      setShopMappingError('Thread non disponibile');
      return;
    }

    if (!formData.gender) {
      setShopMappingError('Seleziona prima il gender');
      return;
    }

    setShopMappingError(null);
    setShopMappingSearchTerm('');  // Reset search term

    // If we already have mapping options, just show the modal
    if (Object.keys(shopMappingOptions).length > 0) {
      // Re-initialize selections from current state
      const initialSelections: Record<string, string[]> = {};
      mpCodNegozioList.forEach((entry, index) => {
        const selectionKey = `entry_${index}`;
        if (Array.isArray(entry)) {
          initialSelections[selectionKey] = [...entry];
        } else {
          // Check if the current value is a valid key
          const isValidKey = shopMappingOptions[entry as string] !== undefined;
          if (isValidKey) {
            initialSelections[selectionKey] = [entry as string];
          } else if (index === 0) {
            // Area negozio - keep as-is
            initialSelections[selectionKey] = [entry as string];
          } else {
            // Not parsable - empty selection
            initialSelections[selectionKey] = [];
          }
        }
      });
      setShopMappingSelections(initialSelections);
      setShowShopMappingModal(true);
      return;
    }

    // Otherwise, load mapping options from API
    setIsLoadingShopMapping(true);

    try {
      const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
      
      if (!messageId) {
        throw new Error('Nessun messaggio disponibile nel thread');
      }

      // Call optim-get-shop-mapping with optim-gender param
      const result = await performAction({
        threadId: tempThreadId,
        messageId: messageId,
        actionName: 'optim-get-shop-mapping',
        params: { 'optim-gender': formData.gender }
      });

      // Result is a dict where keys are backend identifiers and values are display choices
      const mappingOptions = (result as Record<string, string>) || {};
      setShopMappingOptions(mappingOptions);

      // Initialize selections using index-based keys (entry_0, entry_1, etc.)
      // Pre-populate with current values from mpCodNegozioList
      const initialSelections: Record<string, string[]> = {};
      mpCodNegozioList.forEach((entry, index) => {
        const selectionKey = `entry_${index}`;
        if (Array.isArray(entry)) {
          // Entry is already an array of selected shops
          initialSelections[selectionKey] = [...entry];
        } else {
          // Entry is a single string - find matching key or use as-is
          const matchingKey = Object.keys(mappingOptions).find(key => 
            mappingOptions[key] === entry || key === entry
          );
          initialSelections[selectionKey] = matchingKey ? [matchingKey] : (index === 0 ? [entry as string] : []);
        }
      });
      setShopMappingSelections(initialSelections);

      setShowShopMappingModal(true);
    } catch (err) {
      console.error('Error loading shop mapping:', err);
      setShopMappingError(err instanceof Error ? err.message : 'Errore durante il caricamento delle opzioni negozi');
    } finally {
      setIsLoadingShopMapping(false);
    }
  };

  // Toggle shop selection for an entry (using index-based key)
  const toggleShopSelection = (selectionKey: string, shopKey: string) => {
    setShopMappingSelections(prev => {
      const currentSelections = prev[selectionKey] || [];
      const isSelected = currentSelections.includes(shopKey);
      
      return {
        ...prev,
        [selectionKey]: isSelected
          ? currentSelections.filter(k => k !== shopKey)
          : [...currentSelections, shopKey]
      };
    });
  };

  // Confirm and apply shop mapping
  const confirmShopMapping = async () => {
    if (!tempThreadId || !thread.threadData) {
      setShopMappingError('Thread non disponibile');
      return;
    }

    setIsUpdatingShopMapping(true);
    setShopMappingError(null);

    try {
      const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
      
      if (!messageId) {
        throw new Error('Nessun messaggio disponibile nel thread');
      }

      // Build the new cod_negozi_list maintaining the same order as mpCodNegozioList
      // Structure: [areaNegozio, [negozio1, negozio11], negozio2, ...]
      // - First element (index 0) is the "Area Negozio" and stays as a single string (not modifiable)
      // - Other elements: if multiple selections, wrap in array; if single selection, keep as string
      const newCodNegozioList: (string | string[])[] = [];
      
      mpCodNegozioList.forEach((entry, index) => {
        const selectionKey = `entry_${index}`;
        if (index === 0) {
          // First element is "Area Negozio" - always keep as single string (get from entry)
          const areaNegozio = Array.isArray(entry) ? entry[0] : entry;
          newCodNegozioList.push(areaNegozio);
        } else {
          const selectedShops = shopMappingSelections[selectionKey] || [];
          if (selectedShops.length > 1) {
            // Multiple selections - wrap in array
            newCodNegozioList.push([...selectedShops]);
          } else if (selectedShops.length === 1) {
            // Single selection - keep as string
            newCodNegozioList.push(selectedShops[0]);
          } else {
            // No selection - keep the original entry
            newCodNegozioList.push(entry);
          }
        }
      });

      // Call optim-update-mp with the new mapping
      await performAction({
        threadId: tempThreadId,
        messageId: messageId,
        actionName: 'optim-update-mp',
        params: { 
          'cod_negozi_list': newCodNegozioList
        }
      });

      // Update the local state with the structured format (NOT flattened)
      setMpCodNegozioList(newCodNegozioList);
      
      // Also update mpNegozioList with display values (flattened for display)
      const newNegozioList: string[] = [];
      newCodNegozioList.forEach(item => {
        if (Array.isArray(item)) {
          newNegozioList.push(...item.map(key => shopMappingOptions[key] || key));
        } else {
          newNegozioList.push(shopMappingOptions[item] || item);
        }
      });
      setMpNegozioList(newNegozioList);

      // Update unparsable list - check which entries still don't have valid selections
      const stillUnparsable = new Set<number>();
      newCodNegozioList.forEach((entry, index) => {
        if (index === 0) return; // Skip area negozio
        
        if (Array.isArray(entry)) {
          // Has selections - check if they are valid keys
          const allValid = entry.every(key => shopMappingOptions[key] !== undefined);
          if (!allValid && entry.length === 0) {
            stillUnparsable.add(index);
          }
        } else {
          // Single value - check if it's a valid key
          if (shopMappingOptions[entry] === undefined) {
            stillUnparsable.add(index);
          }
        }
      });
      setUnparsableNegozi(stillUnparsable);

      // Clear budget file when shop mapping changes - budget must be re-uploaded
      setFormData(prev => ({ ...prev, budgetDeliveryFile: null }));
      setBudgetTableData(null);
      setBudgetCodNegozioList([]);
      setBudgetCurrentNegozioIndex(0);
      setBudgetTotal(null);
      setBudgetFileError(null);
      setBudgetUploadMessage(null);

      setShowShopMappingModal(false);
    } catch (err) {
      console.error('Error updating shop mapping:', err);
      setShopMappingError(err instanceof Error ? err.message : 'Errore durante l\'aggiornamento del mapping negozi');
    } finally {
      setIsUpdatingShopMapping(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!catalogMode) {
      setError('Seleziona Autunno/Inverno o Primavera/Estate');
      return;
    }

    if (projectType === 'optimization' && catalogModeOnly) {
      onCatalogModeConfirmed?.(catalogMode, incognito);
      resetModal();
      return;
    }

    if (projectType === 'consultation') {
      setIsCreating(true);
      try {
        await dispatch(createNewProject({ 
          title: 'Nuovo Progetto Consultazione',
          creationMetadata: { project_type: 'consultation', catalog_mode: catalogMode || undefined }
        })).unwrap();

        resetModal();
        onProjectCreated();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Errore durante la creazione del progetto');
      } finally {
        setIsCreating(false);
      }
      return;
    }

    // For optimization, validate required fields
    if (shopMode === 'single') {
      if (!formData.negozio || !formData.gender || !formData.season || !formData.consegna || !formData.macrotema) {
        setError('Compila tutti i campi obbligatori');
        return;
      }
    } else {
      // Multi-shop mode - negozio is not required
      if (!formData.gender || !formData.season || !formData.consegna || !formData.macrotema) {
        setError('Compila tutti i campi obbligatori');
        return;
      }
    }

    if (!formData.merchandisingPlanFile || !formData.budgetDeliveryFile) {
      setError('Carica entrambi i file richiesti');
      return;
    }
    
    // Show confirmation popup asking about configuration
    void proceedWithProjectCreation();
    return;
  };

  // Proceed with actual project creation after configuration confirmation
  const proceedWithProjectCreation = async () => {
    setShowConfigConfirmPopup(false);
    setError(null);

    // Use the temp thread we created when loading options
    if (!tempThreadId || !thread.threadData) {
      setError('Thread non disponibile');
      return;
    }

    setIsCreating(true);
    setIsOptimizing(true);
    setOptimizationProgress(0);

    try {
      // Get the message ID from thread state
      const messageId = thread.threadData.local_memory?.thread_state?.['current-message'] as string;
      
      if (!messageId) {
        throw new Error('Nessun messaggio disponibile nel thread');
      }
      
      // Step 1: Set remaining form values via actions (gender, consegna, negozio, section already set during file uploads)
      await performAction({ 
        threadId: tempThreadId, 
        messageId: messageId, 
        actionName: 'optim-set-season',
        params: { 'optim-season': formData.season }
      });

      if (formData.optimizationTags.length > 0) {
        for (const tag of formData.optimizationTags) {
          await performAction({ 
            threadId: tempThreadId, 
            messageId: messageId, 
            actionName: 'optim-set-tag',
            params: { 'optim-tag': tag }
          });
        }
      }

      // Step 2 & 3: Initialize dataset and config (only if not already created for SKU upload)
      if (!configCreated) {
        // Step 2: Initialize dataset (files have already been uploaded)
        const initDatasetParams: Record<string, any> = {
          'optim-cod-stagione': formData.season,
          'optim-consegna': formData.consegna,
          'optim-gender': formData.gender,
        };
        
        // In single shop mode, pass the negozio; in multi-shop, pass the negozio-list
        if (shopMode === 'single') {
          initDatasetParams['optim-cod-negozio'] = formData.negozio;
        } else if (mpCodNegozioList.length > 0) {
          // Use mpCodNegozioList which has the structured format with codes (not display names)
          initDatasetParams['negozio-list'] = mpCodNegozioList;
        }
        
        await performAction({ 
          threadId: tempThreadId, 
          messageId: messageId, 
          actionName: 'optim-form',
          params: { 
            'optim-form-name': 'init-dataset',
            'optim-form-data': initDatasetParams
          }
        });

        // Step 3: Initialize config
        await performAction({ 
          threadId: tempThreadId, 
          messageId: messageId, 
          actionName: 'optim-init-config',
          params: { 
            'optim-form-data': {}
          }
        });
      }

      // Step 4: enter the new merchandising plan workspace
      setIsCreating(false);
      setIsOptimizing(false);
      setOptimizationProgress(0);
      if (embedded) {
        setIsFinalizingConfig(true);
        try {
          if (tempThreadId && Object.keys(shopMappingOptions).length > 0) {
            persistMerchandisingShopMapping(tempThreadId, shopMappingOptions);
          }
          if (tempThreadId) {
            await dispatch(loadProject(tempThreadId)).unwrap();
          }
          await onOptimizationConfigured?.();
        } finally {
          setIsFinalizingConfig(false);
        }
      } else {
        onClose();
        onOpenMerchandisingPlan?.();
        resetModal();
      }
      return;

    } catch (err) {
      console.error('Errore durante la creazione del progetto:', err);
      setError(err instanceof Error ? err.message : 'Errore durante la creazione del progetto');
      setIsCreating(false);
      setIsOptimizing(false);
    }
  };

  // Helper function to convert File to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const resetModal = () => {
    setFormData({
      negozio: '',
      gender: '',
      season: '',
      consegna: '',
      macrotema: '',
      valueColumn: '',
      merchandisingPlanFile: null,
      budgetDeliveryFile: null,
      optimizationTags: [],
    });
    setError(null);
    setMpFileError(null);
    setBudgetFileError(null);
    setMpUploadMessage(null);
    setBudgetUploadMessage(null);
    setMpTableData(null);
    setBudgetTableData(null);
    setMpTotal(null);
    setBudgetTotal(null);
    setBudgetCodNegozioList([]);
    setBudgetCurrentNegozioIndex(0);
    setListinoPerShop({});
    setEditedListinoPerShop({});
    setAvailableListini(null);
    setListinoValidated(false);
    setListinoCurrencyWarning(null);
    setListinoError(null);
    setAvailableValueColumns([]);
    setSkuListData(null);
    setSkuListError(null);
    setSkuListMessage(null);
    setShowMpTableModal(false);
    setShowBudgetTableModal(false);
    setShowSkuListModal(false);
    setShowAddProductModal(false);
    setShowSkuTextArea(false);
    setPastedSkuText('');
    setShowSkuConfirmation(false);
    setIsSkuPerimeterOnly(false);
    setIsValidatingSkus(false);
    setInvalidSkus(new Set());
    setAvailableSkus([]);
    setSkuValidationDone(false);
    setOptimizationProgress(0);
    setIsOptimizing(false);
    setIsCreating(false);
    setCatalogMode('');
    setIncognito(initialIncognito);
    setIsFinalizingConfig(false);
    setTempThreadId(null);
    setPreviousThreadId(null);
    setConfigCreated(false);
    setShopMode('single');
    setMpNegozioList([]);
    setMpCodNegozioListOriginal([]);
    setMpCodNegozioList([]);
    setMpCurrentNegozioIndex(0);
    // Reset config popup state
    setShowConfigPopup(false);
    setShowConfigConfirmPopup(false);
    setConstraints(null);
    setIsLoadingConfig(false);
    setTipoFilatoOverrides([]);
    setTipoFilatoEnum([]);
    // Reset shop mapping state
    setShowShopMappingModal(false);
    setShopMappingOptions({});
    setShopMappingSelections({});
    setIsLoadingShopMapping(false);
    setIsUpdatingShopMapping(false);
    setShopMappingError(null);
    setShopMappingSearchTerm('');
    setUnparsableNegozi(new Set());
    setNegoziParsingDone(false);
  };

  const handleCancel = async () => {
    // If we created a temp thread for optimization and have a previous thread to return to
    if (projectType === 'optimization' && tempThreadId && previousThreadId) {
      try {
        // Delete the temporary thread
        await deleteThread(tempThreadId);
        
        // Return to the previous thread
        await dispatch(loadProject(previousThreadId)).unwrap();
      } catch (err) {
        console.error('Error during cancel:', err);
        // Even if there's an error, try to return to previous thread
        if (previousThreadId) {
          try {
            await dispatch(loadProject(previousThreadId)).unwrap();
          } catch (loadErr) {
            console.error('Error loading previous thread:', loadErr);
          }
        }
      }
    }
    
    resetModal();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setCatalogMode(initialCatalogMode);
    setIncognito(initialIncognito);
  }, [initialCatalogMode, initialIncognito, isOpen]);

  if (!isOpen) return null;

  const isCatalogModeSelectionStep = projectType === 'consultation' || (projectType === 'optimization' && catalogModeOnly);

  return (
    <div className={embedded ? 'flex min-h-0 flex-1 flex-col' : 'fixed inset-0 overlay-in z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'}>
      <div className={`${embedded ? 'w-full min-h-0 flex-1 self-stretch' : isCatalogModeSelectionStep ? 'max-w-2xl w-full bg-white shadow-xl' : 'max-w-5xl w-full max-h-[90vh] bg-white shadow-xl'} relative flex flex-col`}>
        {!embedded && (
          <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
            <h2 className="text-xl font-semibold text-gray-900">
              {`Nuovo Progetto - ${projectType === 'optimization' ? 'Ottimizzazione' : 'Consultazione'}`}
            </h2>
          </div>
        )}

        {/* Loading overlay while fetching options */}
        {projectType === 'optimization' && (isCreating || isOptimizing || isFinalizingConfig) && (
          <div className="absolute inset-0 z-20 animate-fade-in flex items-center justify-center bg-white/75 backdrop-blur-[1px]">
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-gray-200 bg-white px-6 py-5 shadow-lg">
              <svg className="h-8 w-8 animate-spin text-[#9F6460]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <div className="text-sm font-semibold text-gray-900">
                {isFinalizingConfig ? 'CARICAMENTO DATI...' : 'CONFIGURAZIONE OTTIMIZZAZIONE...'}
              </div>
              <div className="text-xs text-gray-500">
                Attendi il completamento della configurazione.
              </div>
            </div>
          </div>
        )}

        {projectType === 'optimization' && isLoadingOptions && (
          <div className="flex-1 p-6 flex flex-col items-center justify-center">
            <div className="relative">
              <svg className="animate-spin-in h-16 w-16 text-[#9F6460]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <p className="mt-6 text-lg font-medium text-gray-900">Caricamento opzioni...</p>
            <p className="mt-2 text-sm text-gray-600">Attendere prego, stiamo preparando il modulo di ottimizzazione</p>
          </div>
        )}

        {/* Form - only show when not loading options */}
        {(!isLoadingOptions || projectType !== 'optimization') && (
          <form id={formId} onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-auto p-6">
          {isCatalogModeSelectionStep ? (
            <div className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  FAMIGLIA STAGIONALE
                </label>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {CATALOG_MODES.map((mode) => {
                    const selected = catalogMode === mode;
                    const color = mode === 'AI' ? '#688096' : '#9F6460';
                    return (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => {
                          setCatalogMode(mode);
                          setError(null);
                        }}
                        disabled={isCreating}
                        className={`group relative overflow-hidden border-2 p-6 text-left transition-all duration-300 ${
                          selected
                            ? 'border-black bg-black text-white'
                            : 'border-gray-200 bg-white text-gray-900 hover:border-[var(--card-accent)]'
                        } ${isCreating ? 'cursor-not-allowed opacity-50' : ''}`}
                        style={{ ['--card-accent' as string]: color }}
                      >
                        <div className="flex h-full flex-col">
                          <div className="mb-3 flex items-center gap-3">
                            <div
                              className={`flex h-10 w-10 items-center justify-center border text-sm font-semibold ${
                                selected ? 'border-white/30 bg-white/10 text-white' : 'border-gray-200 bg-gray-50 text-gray-700'
                              }`}
                            >
                              {mode}
                            </div>
                            <h3 className={`text-xl font-bold ${selected ? 'text-white' : 'text-gray-900'}`}>
                              {getCatalogModeLabel(mode)}
                            </h3>
                          </div>
                          <p className={`mb-4 flex-1 text-sm ${selected ? 'text-white/80' : 'text-gray-600'}`}>
                            {mode === 'AI' ? 'Usa il catalogo Autunno/Inverno per continuare.' : 'Usa il catalogo Primavera/Estate per continuare.'}
                          </p>
                          <div className={`flex items-center text-sm font-semibold transition-transform duration-300 ${selected ? 'text-white' : ''} group-hover:translate-x-2`} style={selected ? undefined : { color }}>
                            <span>Seleziona</span>
                            <i className="ti-arrow-right ml-2" aria-hidden="true" />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {projectType === 'optimization' && catalogModeOnly && isAdmin && (
                <label className="flex cursor-pointer items-start gap-3 border border-gray-200 bg-gray-50 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={incognito}
                    onChange={(e) => setIncognito(e.target.checked)}
                    disabled={isCreating}
                    className="mt-0.5 h-4 w-4 accent-[#9F6460]"
                  />
                  <span className="text-sm">
                    <span className="font-semibold text-gray-900">Ottimizzazione in incognito</span>
                    <span className="mt-0.5 block text-xs text-gray-500">
                      Progetto di test visibile solo agli admin: non comparirà nella lista degli altri utenti.
                    </span>
                  </span>
                </label>
              )}

              <p className="text-gray-700">
                {projectType === 'consultation'
                  ? 'Verrà creato un nuovo progetto di consultazione. Clicca "Crea Progetto" per continuare.'
                  : 'Seleziona la famiglia stagionale per continuare con la configurazione del progetto di ottimizzazione.'}
              </p>

              {error && (
                <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}
            </div>
          ) : (
            // Full form for optimization
            <div className="space-y-6">
              {/* Shop Mode Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  MODALITÀ OTTIMIZZAZIONE
                </label>
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 px-4 py-3 border cursor-pointer transition-colors ${
                    shopMode === 'single' 
                      ? 'border-[#9F6460] bg-[#9F6460]/5 text-[#9F6460]' 
                      : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                  } ${isCreating || isLoadingOptions || !catalogMode ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <input
                      type="radio"
                      name="shopMode"
                      value="single"
                      checked={shopMode === 'single'}
                      onChange={() => handleShopModeChange('single')}
                      className="w-4 h-4 text-[#9F6460] border-gray-300 focus:ring-[#9F6460]"
                      disabled={isCreating || isLoadingOptions || !catalogMode}
                    />
                    <span className="text-sm font-medium">Singolo negozio</span>
                  </label>
                  <label className={`flex items-center gap-2 px-4 py-3 border cursor-pointer transition-colors ${
                    shopMode === 'multi' 
                      ? 'border-[#9F6460] bg-[#9F6460]/5 text-[#9F6460]' 
                      : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                  } ${isCreating || isLoadingOptions || !catalogMode ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <input
                      type="radio"
                      name="shopMode"
                      value="multi"
                      checked={shopMode === 'multi'}
                      onChange={() => handleShopModeChange('multi')}
                      className="w-4 h-4 text-[#9F6460] border-gray-300 focus:ring-[#9F6460]"
                      disabled={isCreating || isLoadingOptions || !catalogMode}
                    />
                    <span className="text-sm font-medium">Ottimizzazione per Area</span>
                  </label>
                </div>
              </div>

              {/* Negozio Selezionato - only shown for single shop mode */}
              {shopMode === 'single' && (
                <div className="w-1/2">
                  <label htmlFor="negozio" className="block text-sm font-medium text-gray-700 mb-1">
                    NEGOZIO SELEZIONATO
                  </label>
                  <ShopPicker
                    options={shopPickerOptions}
                    value={formData.negozio}
                    onChange={(code) => {
                      const updates = clearDependentFields('negozio');
                      setFormData(prev => ({ ...prev, negozio: code, ...updates }));
                    }}
                    disabled={isCreating || isLoadingOptions}
                  />
                </div>
              )}

              {/* Consegna Target - 4 selettori sulla stessa riga: Stagione, Consegna, Macrotema, Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CONSEGNA TARGET
                </label>
                <div className="grid grid-cols-4 gap-3">
                  {/* Stagione - richiede Negozio in single mode, always available in multi mode */}
                  <select
                    value={formData.season}
                    onChange={(e) => {
                      const updates = clearDependentFields('season');
                      setFormData(prev => ({ ...prev, season: e.target.value, ...updates }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 focus:border-[#9F6460] focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={isCreating || isLoadingOptions || (shopMode === 'single' && !formData.negozio)}
                  >
                    <option value="">Stagione</option>
                    {availableSeasons.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  
                  {/* Consegna - richiede Stagione */}
                  <select
                    value={formData.consegna}
                    onChange={(e) => {
                      const updates = clearDependentFields('consegna');
                      setFormData(prev => ({ ...prev, consegna: e.target.value, ...updates }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 focus:border-[#9F6460] focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={isCreating || isLoadingOptions || !formData.season}
                  >
                    <option value="">Consegna</option>
                    {availableConsegne.map(c => (
                      <option key={c} value={c}>{getConsegnaDisplayLabel(c)}</option>
                    ))}
                  </select>
                  
                  {/* Macrotema - richiede Consegna */}
                  <select
                    value={formData.macrotema}
                    onChange={(e) => {
                      const updates = clearDependentFields('macrotema');
                      setFormData(prev => ({ ...prev, macrotema: e.target.value, ...updates }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 focus:border-[#9F6460] focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={isCreating || isLoadingOptions || !formData.consegna}
                  >
                    <option value="">Macrotema</option>
                    {availableMacrotemi.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  
                  {/* Gender - richiede Macrotema */}
                  <select
                    value={formData.gender}
                    onChange={(e) => {
                      const updates = clearDependentFields('gender');
                      setFormData(prev => ({ ...prev, gender: e.target.value, ...updates }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 focus:border-[#9F6460] focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={isCreating || isLoadingOptions || !formData.macrotema}
                  >
                    <option value="">Linea</option>
                    {availableGenders.map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Colonna Valore - shown after gender is selected */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  COLONNA VALORE
                </label>
                <div className="flex items-center gap-3">
                  <select
                    value={formData.valueColumn}
                    onChange={(e) => handleValueColumnChange(e.target.value)}
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 focus:border-[#9F6460] focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    disabled={isCreating || isLoadingValueColumns || !formData.gender || availableValueColumns.length === 0}
                  >
                    <option value="">Seleziona colonna valore</option>
                    {availableValueColumns.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                  {isLoadingValueColumns && (
                    <svg className="animate-spin h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                </div>
              </div>

              {/* File Merchandising Plan */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  FILE MERCHANDISING PLAN
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    id="merchandisingPlan"
                    onChange={handleFileChange('merchandisingPlanFile')}
                    className="hidden"
                    accept=".xlsx,.xls,.csv"
                    disabled={isCreating || isUploadingMp || !formData.gender || !formData.consegna || !formData.valueColumn}
                  />
                  <label
                    htmlFor="merchandisingPlan"
                    className={`px-4 py-2 border border-gray-300 text-sm transition-colors flex items-center gap-2 ${
                      isCreating || isUploadingMp || !formData.gender || !formData.consegna || !formData.valueColumn
                        ? 'opacity-50 cursor-not-allowed bg-gray-100'
                        : 'hover:bg-gray-50 cursor-pointer text-gray-700'
                    }`}
                  >
                    {isUploadingMp && (
                      <svg className="animate-spin h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isUploadingMp ? 'CARICAMENTO...' : 'SFOGLIA...'}
                  </label>
                  {formData.merchandisingPlanFile && (
                    <div className="flex items-center justify-between flex-1">
                      <span className="text-sm text-gray-700 flex items-center gap-2">
                        <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        {formData.merchandisingPlanFile.name}
                      </span>
                      {/* Button to view MP data - aligned right */}
                      {mpTableData && Object.keys(mpTableData).length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowMpTableModal(true)}
                          className="px-4 py-2 text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                          </svg>
                          Visualizza dati Merchandising Plan
                        </button>
                      )}
                    </div>
                  )}
                  </div>
                  {(mpFileError || mpUploadMessage) && (
                    <div className={`mt-3 p-4 rounded-lg shadow-sm border ${
                      mpFileError 
                        ? 'bg-gradient-to-r from-red-50 to-red-100 border-red-200 text-red-800' 
                        : 'bg-gradient-to-r from-green-50 to-green-100 border-green-200 text-green-800'
                    }`}>
                      <div className="flex items-start space-x-3">
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                          mpFileError ? 'bg-red-200' : 'bg-green-200'
                        }`}>
                          {mpFileError ? '✗' : '✓'}
                        </div>
                        <div className="flex-1">
                          <div className={`text-sm font-medium mb-1 ${
                            mpFileError ? 'text-red-900' : 'text-green-900'
                          }`}>
                            {mpFileError ? 'Upload Error' : 'Success'}
                          </div>
                          <div className={`text-sm whitespace-pre-line ${
                            mpFileError ? 'text-red-700' : 'text-green-700'
                          }`}>
                            {mpFileError || mpUploadMessage}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Negozio List from MP (multi-shop mode) */}
                  {shopMode === 'multi' && mpNegozioList.length > 0 && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium text-gray-900">Negozi estratti dal file MP</div>
                        <button
                          type="button"
                          onClick={handleShopMappingClick}
                          disabled={isLoadingShopMapping || !formData.gender}
                          className="px-3 py-1 text-xs border border-[#9F6460] text-[#9F6460] hover:bg-[#9F6460]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        >
                          {isLoadingShopMapping ? (
                            <>
                              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Caricamento...
                            </>
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                              Modifica Mapping
                            </>
                          )}
                        </button>
                      </div>
                      {isLoadingShopMapping ? (
                        <div className="flex items-center gap-2 py-2">
                          <svg className="animate-spin h-4 w-4 text-[#9F6460]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm text-gray-600">Analisi mapping negozi in corso...</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex flex-wrap items-center gap-1">
                            <span className="text-gray-500 text-xs">[</span>
                            {mpCodNegozioList.map((entry, idx) => {
                              const isAreaNegozio = idx === 0;
                              const isArray = Array.isArray(entry);
                              const isUnparsable = unparsableNegozi.has(idx);
                              const displayValue = isArray 
                                ? entry.map(e => shopMappingOptions[e] || e)
                                : (shopMappingOptions[entry as string] || entry);
                              
                              return (
                                <span key={idx} className="flex items-center">
                                  {isArray ? (
                                    <span className="flex items-center gap-0.5">
                                      <span className="text-blue-500 text-xs">[</span>
                                      {(entry as string[]).map((subEntry, subIdx) => (
                                        <span key={subIdx} className="flex items-center">
                                          <span 
                                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded border border-blue-300"
                                          >
                                            {shopMappingOptions[subEntry] || subEntry}
                                          </span>
                                          {subIdx < (entry as string[]).length - 1 && <span className="text-gray-400 text-xs mx-0.5">,</span>}
                                        </span>
                                      ))}
                                      <span className="text-blue-500 text-xs">]</span>
                                    </span>
                                  ) : (
                                    <span 
                                      className={`px-2 py-1 text-xs font-medium rounded border ${
                                        isAreaNegozio 
                                          ? 'bg-amber-100 text-amber-800 border-amber-300' 
                                          : isUnparsable
                                            ? 'bg-red-100 text-red-800 border-red-300'
                                            : 'bg-green-100 text-green-800 border-green-300'
                                      }`}
                                      title={isAreaNegozio ? 'Area Negozio' : isUnparsable ? 'Non riconosciuto - Assegnare manualmente' : ''}
                                    >
                                      {isAreaNegozio && <span className="text-amber-600 mr-1">★</span>}
                                      {isUnparsable && <span className="text-red-600 mr-1">⚠</span>}
                                      {displayValue as string}
                                    </span>
                                  )}
                                  {idx < mpCodNegozioList.length - 1 && <span className="text-gray-400 text-xs mx-1">,</span>}
                                </span>
                              );
                            })}
                            <span className="text-gray-500 text-xs">]</span>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-amber-100 border border-amber-300 rounded"></span> Area Negozio</span>
                            <span className="mx-2">|</span>
                            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-green-100 border border-green-300 rounded"></span> Riconosciuto</span>
                            <span className="mx-2">|</span>
                            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-red-100 border border-red-300 rounded"></span> Non riconosciuto</span>
                            <span className="mx-2">|</span>
                            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></span> Multipli</span>
                          </div>
                          {unparsableNegozi.size > 0 && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                              <p className="text-xs text-red-700 font-medium">
                                ⚠ {unparsableNegozi.size} negozio/i non riconosciuto/i. È necessario assegnare manualmente i negozi prima di procedere.
                              </p>
                              <p className="text-xs text-red-600 mt-1">
                                Clicca "Modifica Mapping" per assegnare i negozi mancanti.
                              </p>
                            </div>
                          )}
                        </>
                      )}
                      {!formData.gender && (
                        <p className="mt-2 text-xs text-amber-600">Seleziona prima il gender per analizzare il mapping dei negozi</p>
                      )}
                    </div>
                  )}
                  
                  {/* MP Total Info */}
                  {mpTotal !== null && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200">
                      <div className="text-sm font-medium text-gray-900 mb-2">Totale Merchandising Plan</div>
                      <div className="text-sm text-gray-700">
                        Totale: <span className="font-semibold">{mpTotal.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  )}
              </div>

              {/* File Budget Delivery */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  FILE BUDGET DELIVERY
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    id="budgetDelivery"
                    onChange={handleFileChange('budgetDeliveryFile')}
                    className="hidden"
                    accept=".xlsx,.xls,.csv"
                    disabled={
                      isCreating || 
                      isUploadingBudget || 
                      !formData.macrotema || 
                      !formData.gender || 
                      !formData.consegna ||
                      (shopMode === 'single' && !formData.negozio) ||
                      !formData.merchandisingPlanFile ||
                      !mpTableData
                    }
                  />
                  <label
                    htmlFor="budgetDelivery"
                    className={`px-4 py-2 border border-gray-300 text-sm transition-colors flex items-center gap-2 ${
                      isCreating || 
                      isUploadingBudget || 
                      !formData.macrotema || 
                      !formData.gender || 
                      !formData.consegna ||
                      (shopMode === 'single' && !formData.negozio) ||
                      !formData.merchandisingPlanFile ||
                      !mpTableData
                        ? 'opacity-50 cursor-not-allowed bg-gray-100'
                        : 'hover:bg-gray-50 cursor-pointer text-gray-700'
                    }`}
                  >
                    {isUploadingBudget && (
                      <svg className="animate-spin h-4 w-4 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isUploadingBudget ? 'CARICAMENTO...' : 'SFOGLIA...'}
                  </label>
                  {formData.budgetDeliveryFile && (
                    <div className="flex items-center justify-between flex-1">
                      <span className="text-sm text-gray-700 flex items-center gap-2">
                        <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        {formData.budgetDeliveryFile.name}
                      </span>
                      {/* Button to view Budget data - aligned right */}
                      {budgetTableData && Object.keys(budgetTableData).length > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowBudgetTableModal(true)}
                          className="px-4 py-2 text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 whitespace-nowrap"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                            </svg>
                          Visualizza dati Budget Delivery
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {(budgetFileError || budgetUploadMessage) && (
                  <div className={`mt-3 p-4 rounded-lg shadow-sm border ${
                    budgetFileError 
                      ? 'bg-gradient-to-r from-red-50 to-red-100 border-red-200 text-red-800' 
                      : 'bg-gradient-to-r from-green-50 to-green-100 border-green-200 text-green-800'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                        budgetFileError ? 'bg-red-200' : 'bg-green-200'
                      }`}>
                        {budgetFileError ? '✗' : '✓'}
                      </div>
                      <div className="flex-1">
                        <div className={`text-sm font-medium mb-1 ${
                          budgetFileError ? 'text-red-900' : 'text-green-900'
                        }`}>
                          {budgetFileError ? 'Upload Error' : 'Success'}
                        </div>
                        <div className={`text-sm whitespace-pre-line ${
                          budgetFileError ? 'text-red-700' : 'text-green-700'
                        }`}>
                          {budgetFileError || budgetUploadMessage}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Budget Total Info */}
                {budgetTotal !== null && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200">
                    <div className="text-sm font-medium text-gray-900 mb-2">Totale Budget Delivery</div>
                    <div className="text-sm text-gray-700">
                      Totale: <span className="font-semibold">{budgetTotal.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                )}

                {/* Nessun listino presente per la stagione/collezione selezionata */}
                {listiniNotAvailable && !isLoadingListino && Object.keys(listinoPerShop).length === 0 && (
                  <div className="mt-3 p-3 border bg-red-50 border-red-200">
                    <div className="flex items-start gap-2">
                      <svg className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div className="text-sm text-red-800">
                        <div className="font-medium">Listini non presenti</div>
                        <div className="text-xs mt-1">
                          Non risultano listini per la stagione {formData.season} in questa collezione. Verifica che i dati dei prezzi/listini della stagione siano stati caricati prima di procedere.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Listino Configuration - shown after budget upload */}
                {Object.keys(listinoPerShop).length > 0 && (
                  <div className={`mt-3 p-3 border ${listinoValidated ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-medium text-gray-900">
                        Configurazione Listini
                        {listinoValidated && (
                          <span className="ml-2 text-green-600 text-xs">✓ Confermato</span>
                        )}
                      </div>
                      {isLoadingListino || isLoadingAvailableListini ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Caricamento...
                        </div>
                      ) : null}
                    </div>

                    {/* Currency mismatch warning */}
                    {listinoCurrencyWarning && (
                      <div className="mb-3 p-2 bg-amber-100 border border-amber-300 rounded">
                        <div className="flex items-start gap-2">
                          <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                          </svg>
                          <span className="text-xs text-amber-800">{listinoCurrencyWarning}</span>
                        </div>
                      </div>
                    )}

                    {/* Error message */}
                    {listinoError && (
                      <div className="mb-3 p-2 bg-red-100 border border-red-300 rounded">
                        <span className="text-xs text-red-800">{listinoError}</span>
                      </div>
                    )}

                    {/* Listino selection per shop - SPJ-1727: tabella compatta, una riga per negozio,
                        con ricerca e filtro nazione (SPJ-1728) quando i negozi sono tanti. */}
                    {(() => {
                      const listinoEntries = Object.entries(editedListinoPerShop);
                      const listinoRows = listinoEntries.map(([shopId, listino]) => {
                        const codes = shopId.split('|,|').map((c) => c.trim()).filter(Boolean);
                        return {
                          shopId,
                          listino,
                          option: {
                            code: codes.join(', '),
                            name: getShopDisplayName(shopId),
                            nazione: codes.length === 1 ? shopNazione[codes[0]] : undefined,
                          },
                        };
                      });
                      const visibleRows = listinoRows.filter((r) => matchesShop(r.option, listiniSearchTerm, listiniNazione));
                      const renderListinoSelect = (shopId: string, kind: 'rtl' | 'whs', current: Listino) => (
                        <select
                          aria-label={`Listino ${kind.toUpperCase()} ${shopId}`}
                          value={`${current.listino}|${current.cod_moneta}`}
                          onChange={(e) => {
                            const parts = e.target.value.split('|');
                            const codMoneta = parts.pop() || '';
                            const listinoName = parts.join('|'); // In case listino name contains |
                            handleListinoChange(shopId, kind, { listino: listinoName, cod_moneta: codMoneta });
                          }}
                          className="w-full px-2 py-1 text-xs border border-gray-300 focus:border-[#9F6460] focus:outline-none disabled:bg-gray-100"
                          disabled={isUpdatingListino || !availableListini}
                        >
                          {(kind === 'rtl' ? availableListini?.listini_rtl : availableListini?.listini_whs)?.map((l) => (
                            <option key={`${l.listino}|${l.cod_moneta}`} value={`${l.listino}|${l.cod_moneta}`}>
                              {l.listino} ({l.cod_moneta})
                            </option>
                          ))}
                        </select>
                      );
                      return (
                        <>
                          {listinoRows.length > 1 && (
                            <div className="mb-2 flex items-center gap-2">
                              <input
                                type="text"
                                placeholder="Cerca negozio..."
                                value={listiniSearchTerm}
                                onChange={(e) => setListiniSearchTerm(e.target.value)}
                                className="min-w-0 flex-1 border border-gray-300 px-2 py-1 text-xs focus:border-[#9F6460] focus:outline-none"
                              />
                              <NazioneFilter nazioni={nazioniOf(listinoRows.map((r) => r.option))} value={listiniNazione} onChange={setListiniNazione} />
                              <span className="text-xs text-gray-500">{visibleRows.length}/{listinoRows.length}</span>
                            </div>
                          )}
                          <div className="max-h-80 overflow-y-auto border border-gray-200 bg-white">
                            <table className="w-full text-xs">
                              <thead className="sticky top-0 bg-gray-50 text-left text-gray-500">
                                <tr>
                                  <th className="px-2 py-1.5 font-medium">Negozio</th>
                                  <th className="w-[30%] px-2 py-1.5 font-medium">Listino RTL</th>
                                  <th className="w-[30%] px-2 py-1.5 font-medium">Listino WHS</th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleRows.map(({ shopId, listino, option }) => (
                                  <tr key={shopId} className="border-t border-gray-100">
                                    <td className="px-2 py-1.5">
                                      <span className="mr-2 font-mono font-semibold text-gray-900">{option.code}</span>
                                      <span className="text-gray-700">{option.name}</span>
                                      {option.nazione && <span className="ml-2 text-[10px] text-gray-400">{option.nazione}</span>}
                                    </td>
                                    <td className="px-2 py-1">{renderListinoSelect(shopId, 'rtl', listino.listino_rtl)}</td>
                                    <td className="px-2 py-1">{renderListinoSelect(shopId, 'whs', listino.listino_whs)}</td>
                                  </tr>
                                ))}
                                {visibleRows.length === 0 && (
                                  <tr><td colSpan={3} className="px-2 py-3 text-center italic text-gray-500">Nessun negozio trovato con questi filtri</td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </>
                      );
                    })()}

                    {/* Confirm button */}
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={handleConfirmListini}
                        disabled={isUpdatingListino || listinoValidated}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                          listinoValidated
                            ? 'bg-green-100 text-green-700 border border-green-300 cursor-default'
                            : isUpdatingListino
                              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                              : 'bg-[#9F6460] text-white hover:bg-[#8a5653]'
                        }`}
                      >
                        {isUpdatingListino ? (
                          <span className="flex items-center gap-2">
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Salvataggio...
                          </span>
                        ) : listinoValidated ? (
                          '✓ Listini Confermati'
                        ) : (
                          'Conferma Listini'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>


              {/* Cosa vuoi ottimizzare? - Tags dinamici */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Cosa vuoi ottimizzare?
                </label>
                {isLoadingOptions ? (
                  <div className="text-sm text-gray-500">Caricamento tags...</div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    {availableTags.map((tag) => (
                      <label key={tag} className={`flex items-center space-x-2 ${
                        isCreating || !formData.merchandisingPlanFile || !formData.budgetDeliveryFile 
                          ? 'cursor-not-allowed opacity-50' 
                          : 'cursor-pointer'
                      }`}>
                        <input
                          type="checkbox"
                          checked={formData.optimizationTags.includes(tag)}
                          onChange={() => handleTagToggle(tag)}
                          className="w-4 h-4 text-[#9F6460] border-gray-300 focus:ring-[#9F6460] disabled:cursor-not-allowed"
                          disabled={isCreating || !formData.merchandisingPlanFile || !formData.budgetDeliveryFile}
                        />
                        <span className="text-sm text-gray-700">{tag}</span>
                      </label>
                    ))}
                  </div> */}
                {/* )} */}
              {/* </div> */}

              {/* Optimization Progress */}
              {isOptimizing && !isFinalizingConfig && (
                <div className="bg-blue-50 border border-blue-200 px-4 py-4">
                  <div className="flex items-center gap-3 text-blue-900">
                    <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <div>
                      <div className="text-sm font-medium">Configurazione in corso...</div>
                      <p className="mt-1 text-xs text-blue-700">Stiamo elaborando i dati, attendere prego...</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                error.startsWith('__FAILED_RUN__') ? (
                  <div className="bg-red-50 border border-red-300 rounded-lg overflow-hidden">
                    <div className="bg-red-100 px-4 py-3 border-b border-red-200 flex items-center gap-2">
                      <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="font-semibold text-red-800">Ottimizzazione Fallita</span>
                    </div>
                    <div className="px-4 py-3 space-y-3">
                      {(() => {
                        const cleanError = error.replace('__FAILED_RUN__', '').replace('__CONFIG_RESET__', '');
                        return cleanError ? (
                          <div className="text-sm">
                            <span className="font-medium text-red-800">Dettaglio errore:</span>
                            <OptimizationFailureCard errorLog={cleanError} className="mt-2" />
                          </div>
                        ) : null;
                      })()}
                      {/* Config reset notice */}
                      {error.includes('__CONFIG_RESET__') && (
                        <div className="bg-amber-50 border border-amber-300 rounded px-3 py-2">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm text-amber-800 font-medium">
                              Il file di configurazione è stato riinizializzato per una nuova run
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="text-sm text-red-700">
                        <p className="font-medium mb-2">Si prega di:</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>Rivedere i parametri di configurazione</li>
                          <li>Verificare i file caricati</li>
                          <li>Riprovare l'ottimizzazione</li>
                        </ul>
                      </div>
                      <p className="text-xs text-red-600 italic border-t border-red-200 pt-2">
                        Se il problema persiste, contattare l'amministratore.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm rounded">
                    {error}
                  </div>
                )
              )}
            </div>
          )}
          </div>

          {/* Buttons */}
          {!hideSubmitButton && (
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-white">
              {!embedded && (
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isCreating || isOptimizing}
                  className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ANNULLA
                </button>
              )}
              <div className="flex items-center gap-4">
                {projectType === 'optimization' && Object.keys(listinoPerShop).length > 0 && !listinoValidated && (
                  <span className="text-xs text-amber-600 flex items-center gap-1">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                    </svg>
                    Conferma prima i listini
                  </span>
                )}
                <button
                  type="submit"
                  disabled={isOptimizationSubmitDisabled}
                  className="px-6 py-2 bg-black text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {(isCreating || isOptimizing || isFinalizingConfig) && (
                    <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {isFinalizingConfig
                    ? 'CARICAMENTO DATI...'
                    : isOptimizing || isCreating
                      ? 'CONFIGURAZIONE IN CORSO...'
                      : projectType === 'consultation'
                        ? 'CREA PROGETTO'
                        : catalogModeOnly
                          ? 'CONTINUA'
                          : 'CONFIGURA PERIMETRO & BUDGET'}
                </button>
              </div>
            </div>
          )}
        </form>
        )}
      </div>

      {/* MP Table Modal */}
      {showMpTableModal && (
        <div className="fixed inset-0 overlay-in z-[60] flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowMpTableModal(false)}>
          <div className="bg-white max-w-4xl w-full mx-4 shadow-xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Dati Merchandising Plan</h3>
              <button
                onClick={() => setShowMpTableModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            {/* Pagination by cod_negozio - only show if more than one negozio */}
            {mpCodNegozioListOriginal.length > 1 && (
              <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Negozio:</span>
                    <span className="text-sm font-semibold text-gray-900">{getShopDisplayName(mpCodNegozioListOriginal[mpCurrentNegozioIndex])}</span>
                    <span className="text-xs text-gray-500">({mpCurrentNegozioIndex + 1} di {mpCodNegozioListOriginal.length})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setMpCurrentNegozioIndex(prev => Math.max(0, prev - 1))}
                      disabled={mpCurrentNegozioIndex === 0}
                      className="px-3 py-1 border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      ← Precedente
                    </button>
                    <select
                      value={mpCurrentNegozioIndex}
                      onChange={(e) => setMpCurrentNegozioIndex(Number(e.target.value))}
                      className="px-3 py-1 border border-gray-300 text-sm focus:border-[#9F6460] focus:outline-none"
                    >
                      {mpCodNegozioListOriginal.map((negozio, idx) => (
                        <option key={idx} value={idx}>{getShopDisplayName(negozio)}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setMpCurrentNegozioIndex(prev => Math.min(mpCodNegozioListOriginal.length - 1, prev + 1))}
                      disabled={mpCurrentNegozioIndex === mpCodNegozioListOriginal.length - 1}
                      className="px-3 py-1 border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Successivo →
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="p-6 overflow-auto">
              {mpTableData && mpCodNegozioListOriginal.length > 0 && (
                <div className="border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Tipo Filato
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Items Total
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Currency Value
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Models Unique
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                          SKU Count
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Avg Price
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(mpTableData[mpCodNegozioListOriginal[mpCurrentNegozioIndex]] || []).map((row: MpTableRow, idx: number) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900 font-medium">{row.tipo_filato}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-right">{row.items_total.toLocaleString('it-IT')}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-right">€ {row.currency_value.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-right">{row.models_unique.toLocaleString('it-IT')}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-right">{row.sku_count.toLocaleString('it-IT')}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-right">{row.avg_price.toLocaleString('it-IT')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Table footer with totals for current negozio */}
                  {(mpTableData[mpCodNegozioListOriginal[mpCurrentNegozioIndex]] || []).length > 0 && (
                    <div className="bg-gray-100 px-6 py-3 border-t border-gray-200">
                      <div className="flex justify-between items-center text-sm">
                        <span className="font-medium text-gray-700">
                          Totale {getShopDisplayName(mpCodNegozioListOriginal[mpCurrentNegozioIndex])}:
                        </span>
                        <span className="font-semibold text-gray-900">
                          € {(mpTableData[mpCodNegozioListOriginal[mpCurrentNegozioIndex]] || [])
                            .reduce((sum: number, row: MpTableRow) => sum + row.currency_value, 0)
                            .toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Empty state */}
              {(!mpTableData || mpCodNegozioListOriginal.length === 0 || !(mpTableData[mpCodNegozioListOriginal[mpCurrentNegozioIndex]] || []).length) && (
                <div className="text-center py-8 text-gray-500">
                  Nessun dato disponibile per questo negozio
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowMpTableModal(false)}
                className="px-6 py-2 bg-black text-white hover:bg-gray-800 transition-colors"
              >
                CHIUDI
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Budget Table Modal */}
      {showBudgetTableModal && (
        <div className="fixed inset-0 overlay-in z-[60] flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowBudgetTableModal(false)}>
          <div className="bg-white max-w-3xl w-full mx-4 shadow-xl max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Dati Budget Delivery</h3>
              <button
                onClick={() => setShowBudgetTableModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            
            {/* Pagination by cod_negozio - only show if more than one negozio */}
            {budgetCodNegozioList.length > 1 && (
              <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex flex-col gap-2">
                  {/* Shop name row */}
                  <div className="flex items-start gap-2">
                    <span className="text-sm text-gray-600 flex-shrink-0">Negozio:</span>
                    <span className="text-sm font-semibold text-gray-900 break-words" style={{ wordBreak: 'break-word' }}>
                      {getShopDisplayName(budgetCodNegozioList[budgetCurrentNegozioIndex])}
                    </span>
                    <span className="text-xs text-gray-500 flex-shrink-0">({budgetCurrentNegozioIndex + 1} di {budgetCodNegozioList.length})</span>
                  </div>
                  {/* Navigation row */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setBudgetCurrentNegozioIndex(prev => Math.max(0, prev - 1))}
                      disabled={budgetCurrentNegozioIndex === 0}
                      className="px-3 py-1 border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      ← Precedente
                    </button>
                    <select
                      value={budgetCurrentNegozioIndex}
                      onChange={(e) => setBudgetCurrentNegozioIndex(Number(e.target.value))}
                      className="px-3 py-1 border border-gray-300 text-sm focus:border-[#9F6460] focus:outline-none max-w-[250px] truncate"
                    >
                      {budgetCodNegozioList.map((negozio, idx) => (
                        <option key={idx} value={idx}>{getShopDisplayName(negozio)}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setBudgetCurrentNegozioIndex(prev => Math.min(budgetCodNegozioList.length - 1, prev + 1))}
                      disabled={budgetCurrentNegozioIndex === budgetCodNegozioList.length - 1}
                      className="px-3 py-1 border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Successivo →
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="p-6 overflow-auto">
              {budgetTableData && budgetCodNegozioList.length > 0 && (
                <div className="border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Sotto Consegna
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Items
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(budgetTableData[budgetCodNegozioList[budgetCurrentNegozioIndex]] || []).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900 font-medium">{row.sotto_consegna}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-right">{row.items.toLocaleString('it-IT')}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 text-right">{row.value.toLocaleString('it-IT')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Table footer with totals for current negozio */}
                  {(budgetTableData[budgetCodNegozioList[budgetCurrentNegozioIndex]] || []).length > 0 && (
                    <div className="bg-gray-100 px-6 py-3 border-t border-gray-200">
                      <div className="flex justify-between items-start gap-4 text-sm">
                        <span className="font-medium text-gray-700 break-words" style={{ wordBreak: 'break-word', flex: '1 1 auto' }}>
                          Totale {getShopDisplayName(budgetCodNegozioList[budgetCurrentNegozioIndex])}:
                        </span>
                        <span className="font-semibold text-gray-900 flex-shrink-0">
                          {(budgetTableData[budgetCodNegozioList[budgetCurrentNegozioIndex]] || [])
                            .reduce((sum, row) => sum + row.value, 0)
                            .toLocaleString('it-IT')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Empty state */}
              {(!budgetTableData || budgetCodNegozioList.length === 0 || !(budgetTableData[budgetCodNegozioList[budgetCurrentNegozioIndex]] || []).length) && (
                <div className="text-center py-8 text-gray-500">
                  Nessun dato disponibile per questo negozio
                </div>
              )}
            </div>
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowBudgetTableModal(false)}
                className="px-6 py-2 bg-black text-white hover:bg-gray-800 transition-colors"
              >
                CHIUDI
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SKU List Modal */}
      {showSkuListModal && (
        <div className="fixed inset-0 overlay-in z-[60] flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowSkuListModal(false)}>
          <div className="bg-white max-w-4xl w-full mx-4 shadow-xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Lista SKU Aggiunti</h3>
              <button
                onClick={() => setShowSkuListModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-auto">
              {skuListData && (() => {
                const hasDescriptionColumn = skuListData.some((row) => !!row.description);
                const hasBoundsColumn = skuListData.some((row) => row.lb !== undefined || row.ub !== undefined);
                return (
                  <div className="border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            #
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                            SKU
                          </th>
                          {hasDescriptionColumn && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Descrizione
                            </th>
                          )}
                          {hasBoundsColumn && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Vincoli
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {skuListData.map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm text-gray-500">{idx + 1}</td>
                            <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                              <div className="flex items-center gap-2">
                                <span>{row.sku}</span>
                                {row.notInPerimeter && (
                                  <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-300 rounded">
                                    out of perimeter
                                  </span>
                                )}
                              </div>
                            </td>
                            {hasDescriptionColumn && (
                              <td className="px-6 py-4 text-sm text-gray-900">{row.description || '-'}</td>
                            )}
                            {hasBoundsColumn && (
                              <td className="px-6 py-4 text-sm text-gray-700">
                                {row.lb === 0 && row.ub === 0 ? (
                                  <span className="text-red-600 font-medium">Escluso</span>
                                ) : row.lb !== undefined && row.ub !== undefined ? (
                                  <span>{`>= ${row.lb} e <= ${row.ub}`}</span>
                                ) : row.lb !== undefined ? (
                                  <span>{`>= ${row.lb}`}</span>
                                ) : row.ub !== undefined ? (
                                  <span>{`<= ${row.ub}`}</span>
                                ) : (
                                  <span>-</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowSkuListModal(false)}
                className="px-6 py-2 bg-black text-white hover:bg-gray-800 transition-colors"
              >
                CHIUDI
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProductModal && (
        <AddProductModal
          isOpen={showAddProductModal}
          onClose={() => setShowAddProductModal(false)}
          onAddProducts={handleAddProducts}
          isOptimizationMode={true}
        />
      )}

      {/* SKU Upload Confirmation Modal */}
      {showSkuConfirmation && (
        <div className="fixed inset-0 overlay-in z-[60] flex items-center justify-center bg-black bg-opacity-50" onClick={handleCancelSkuUpload}>
          <div className="bg-white max-w-lg w-full mx-4 shadow-xl flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {invalidSkus.size > 0 ? 'SKU fuori perimetro rilevati' : 'Conferma Ottimizzazione'}
              </h3>
            </div>
            <div className="p-6 overflow-y-auto">
              {/* Warning message for SKUs outside perimeter */}
              {invalidSkus.size > 0 && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        {invalidSkus.size} SKU fuori perimetro
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        Gli SKU evidenziati non rientrano nel perimetro disponibile per la combinazione Macrotema/Gender/Stagione selezionata,
                        ma verranno comunque usati nell'ottimizzazione.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Success message when all SKUs are valid */}
              {skuValidationDone && invalidSkus.size === 0 && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <svg className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Tutti gli SKU sono validi
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        Confermi che l'ottimizzazione deve essere effettuata solo su questi prodotti?
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* SKU count summary */}
              <div className="mb-4">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">
                    <strong className="text-gray-900">{parseSkuText(pastedSkuText).length}</strong> SKU totali
                  </span>
                  {invalidSkus.size > 0 && (
                    <>
                      <span className="text-green-600">
                        <strong>{parseSkuText(pastedSkuText).length - invalidSkus.size}</strong> nel perimetro
                      </span>
                      <span className="text-amber-600">
                        <strong>{invalidSkus.size}</strong> fuori perimetro
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* SKU list with validation highlighting */}
              <div className="p-3 bg-gray-50 border border-gray-200 rounded max-h-48 overflow-y-auto">
                <div className="flex flex-wrap gap-2">
                  {parseSkuText(pastedSkuText).map((sku, idx) => {
                    const isInvalid = invalidSkus.has(sku);
                    return (
                      <span
                        key={`${sku}-${idx}`}
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          isInvalid
                            ? 'bg-amber-100 text-amber-800 border border-amber-300'
                            : 'bg-green-100 text-green-800 border border-green-300'
                        }`}
                        title={isInvalid ? 'SKU fuori perimetro' : 'SKU nel perimetro'}
                      >
                        {sku}
                        {isInvalid && (
                          <span className="ml-1 font-semibold">(out)</span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancelSkuUpload}
                disabled={isUploadingSkuList}
                className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ANNULLA
              </button>
              
              <button
                type="button"
                onClick={handleConfirmSkuUpload}
                disabled={isUploadingSkuList}
                className="px-6 py-2 bg-black text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploadingSkuList ? 'CARICAMENTO...' : 'CONFERMA E INVIA TUTTI'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shop Mapping Modal */}
      {showShopMappingModal && (
        <div className="fixed inset-0 overlay-in z-[70] flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowShopMappingModal(false)}>
          <div 
            className="bg-white w-[760px] max-w-[95vw] max-h-[80vh] shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ borderRadius: '8px' }}
          >
            {/* Header */}
            <div 
              className="flex items-center justify-between px-4 py-3 border-b border-gray-300"
              style={{ backgroundColor: '#E8EDF2' }}
            >
              <h3 className="text-sm font-semibold text-gray-900">MAPPING NEGOZI</h3>
              <button 
                onClick={() => setShowShopMappingModal(false)}
                className="text-gray-400 text-2xl leading-none hover:text-gray-600"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4" style={{ backgroundColor: '#E8EDF2' }}>
              {shopMappingError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
                  {shopMappingError}
                </div>
              )}
              
              <p className="text-sm text-gray-600 mb-2">
                Per ogni negozio estratto dal file MP, seleziona uno o più negozi di destinazione.
                <br />
                <span className="text-xs text-gray-500">Il primo negozio (Area Negozio) non può essere modificato.</span>
              </p>

              {/* Search input */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Cerca negozio..."
                    value={shopMappingSearchTerm}
                    onChange={(e) => setShopMappingSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 pl-9 border border-gray-300 rounded focus:border-[#9F6460] focus:outline-none text-sm"
                  />
                  <svg 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {shopMappingSearchTerm && (
                    <button
                      type="button"
                      onClick={() => setShopMappingSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <NazioneFilter nazioni={nazioniOf(shopMappingOptionList)} value={shopMappingNazione} onChange={setShopMappingNazione} />
                  {(shopMappingSearchTerm || shopMappingNazione) && (
                    <p className="text-xs text-gray-500">
                      {shopMappingOptionList.filter((o) => matchesShop(o, shopMappingSearchTerm, shopMappingNazione)).length} risultati trovati
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {mpCodNegozioList.map((entry, index) => {
                  const isAreaNegozio = index === 0;
                  const selectionKey = `entry_${index}`;
                  const isArray = Array.isArray(entry);
                  const isUnparsable = unparsableNegozi.has(index);
                  
                  // Get display name for the entry
                  const entryDisplayName = isArray 
                    ? (entry as string[]).map(e => shopMappingOptions[e] ? `${e} - ${shopMappingOptions[e]}` : e).join(', ')
                    : (shopMappingOptions[entry as string] ? `${entry} - ${shopMappingOptions[entry as string]}` : entry);
                  
                  // Get the original entry value for the key (for single string entries)
                  const entryKey = isArray ? `entry_${index}` : `${index}`;

                  // Filter shop options based on search term
                  const filteredOptions = shopMappingOptionList.filter((o) => matchesShop(o, shopMappingSearchTerm, shopMappingNazione));
                  
                  return (
                    <div 
                      key={entryKey}
                      className={`p-4 rounded border ${
                        isAreaNegozio 
                          ? 'bg-gray-100 border-gray-300' 
                          : isUnparsable
                            ? 'bg-red-50 border-red-300'
                            : isArray 
                              ? 'bg-blue-50 border-blue-200' 
                              : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        {isAreaNegozio ? (
                          <span className="text-xs text-white bg-[#9F6460] px-2 py-1 rounded font-medium">AREA NEGOZIO</span>
                        ) : isUnparsable ? (
                          <span className="text-xs text-white bg-red-500 px-2 py-1 rounded font-medium">⚠ NON RICONOSCIUTO #{index}</span>
                        ) : isArray ? (
                          <span className="text-xs text-white bg-blue-500 px-2 py-1 rounded font-medium">GRUPPO #{index}</span>
                        ) : (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">#{index}</span>
                        )}
                        <span className="text-sm font-medium text-gray-900">{entryDisplayName}</span>
                        {!isAreaNegozio && (
                          <span className="text-xs text-gray-500">
                            ({(shopMappingSelections[selectionKey] || []).length} selezionati)
                          </span>
                        )}
                        {isAreaNegozio && (
                          <span className="text-xs text-gray-500 italic ml-2">
                            (non modificabile)
                          </span>
                        )}
                      </div>

                      {/* Warning for unparsable entries */}
                      {isUnparsable && !isAreaNegozio && (
                        <div className="mb-3 p-2 bg-red-100 rounded text-xs text-red-800">
                          <strong>⚠ Attenzione:</strong> Questo negozio non è stato riconosciuto automaticamente. Seleziona manualmente il negozio corretto.
                        </div>
                      )}

                      {/* Show current composition for array entries */}
                      {isArray && !isAreaNegozio && !isUnparsable && (
                        <div className="mb-3 p-2 bg-blue-100 rounded text-xs text-blue-800">
                          <strong>Composizione attuale:</strong> [{(entry as string[]).map(e => shopMappingOptions[e] || e).join(', ')}]
                        </div>
                      )}
                      
                      {isAreaNegozio ? (
                        <div className="text-sm text-gray-500 italic">
                          Questo negozio rappresenta l'area di riferimento e non può essere modificato.
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                            {filteredOptions.map(({ code: key, name: displayValue, nazione }) => {
                              const isSelected = (shopMappingSelections[selectionKey] || []).includes(key);
                              return (
                                <label 
                                  key={key}
                                  className={`flex items-center gap-2 p-2 border rounded cursor-pointer transition-colors ${
                                    isSelected 
                                      ? 'bg-[#9F6460]/10 border-[#9F6460]' 
                                      : 'bg-white border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleShopSelection(selectionKey, key)}
                                    className="w-4 h-4 text-[#9F6460] border-gray-300 rounded focus:ring-[#9F6460]"
                                  />
                                  <span className="shrink-0 font-mono text-xs font-semibold text-gray-900">{key}</span>
                                  <span className="min-w-0 flex-1 truncate text-xs text-gray-700" title={`${key} - ${displayValue}`}>
                                    {displayValue}
                                  </span>
                                  {nazione && <span className="shrink-0 text-[10px] text-gray-400">{nazione}</span>}
                                </label>
                              );
                            })}
                          </div>

                          {filteredOptions.length === 0 && (shopMappingSearchTerm || shopMappingNazione) && (
                            <p className="text-sm text-gray-500 italic">Nessun negozio trovato con questi filtri</p>
                          )}
                          {Object.keys(shopMappingOptions).length === 0 && !shopMappingSearchTerm && (
                            <p className="text-sm text-gray-500 italic">Nessuna opzione disponibile</p>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {mpCodNegozioList.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-8">Nessun negozio da mappare</p>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-4 py-3 border-t border-gray-300 bg-white" style={{ borderRadius: '0 0 8px 8px' }}>
              <button
                type="button"
                onClick={() => setShowShopMappingModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={confirmShopMapping}
                disabled={isUpdatingShopMapping}
                className="px-4 py-2 text-sm bg-black text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUpdatingShopMapping && (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isUpdatingShopMapping ? 'Aggiornamento...' : 'Conferma Mapping'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Confirmation Popup - shown before creating project */}
      {showConfigConfirmPopup && (
        <div className="fixed inset-0 overlay-in z-[70] flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowConfigConfirmPopup(false)}>
          <div 
            className="bg-white w-[450px] shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ borderRadius: '8px' }}
          >
            {/* Header */}
            <div 
              className="flex items-center justify-between px-4 py-3 border-b border-gray-300"
              style={{ backgroundColor: '#E8EDF2' }}
            >
              <h3 className="text-sm font-semibold text-gray-900">CONFIGURAZIONE RUN</h3>
              <button 
                onClick={() => setShowConfigConfirmPopup(false)}
                className="text-gray-400 text-2xl leading-none hover:text-gray-600"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="p-6" style={{ backgroundColor: '#E8EDF2' }}>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-[#9F6460]" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Vuoi modificare la configurazione predefinita dell'ottimizzazione prima di procedere?
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Puoi personalizzare i parametri del solver e i vincoli dell'ottimizzazione.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer with buttons */}
            <div className="flex justify-end gap-3 px-4 py-3 border-t border-gray-300 bg-white" style={{ borderRadius: '0 0 8px 8px' }}>
              <button
                type="button"
                onClick={() => setShowConfigConfirmPopup(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowConfigConfirmPopup(false);
                  // Open config popup to modify settings
                  await handleConfigClick();
                }}
                className="px-4 py-2 text-sm border border-[#9F6460] text-[#9F6460] hover:bg-[#9F6460]/10 transition-colors flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                Modifica Configurazione
              </button>
              <button
                type="button"
                onClick={() => proceedWithProjectCreation()}
                className="px-4 py-2 text-sm bg-black text-white hover:bg-gray-800 transition-colors"
              >
                Usa Default e Procedi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Config Popup Modal */}
      {showConfigPopup && (
        <div className="fixed inset-0 overlay-in z-[70] flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowConfigPopup(false)}>
          <div 
            className="bg-white w-[650px] max-h-[80vh] shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{ borderRadius: '8px' }}
          >
            {/* Header */}
            <div 
              className="flex items-center justify-between px-4 py-3 border-b border-gray-300"
              style={{ backgroundColor: '#E8EDF2' }}
            >
              <h3 className="text-sm font-semibold text-gray-900">CONFIGURAZIONE OTTIMIZZAZIONE</h3>
              <button 
                onClick={() => setShowConfigPopup(false)}
                className="text-gray-400 text-2xl leading-none hover:text-gray-600"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4" style={{ backgroundColor: '#E8EDF2' }}>
              {isLoadingConfig ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <svg className="animate-spin-in h-12 w-12 text-[#9F6460]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="mt-4 text-sm font-medium text-gray-900">Caricamento configurazione...</p>
                </div>
              ) : constraints ? (
                <div className="space-y-4">
                  {/* CONFIGURAZIONI Section */}
                  {(() => {
                    const objective = constraints.objective || {};
                    const preparation = constraints.preparation || {};
                    const solver = constraints.solver || {};
                    
                    const hasConfigs = objective.lambda_plus !== undefined || 
                                      objective.lambda_minus !== undefined || 
                                      objective.rho_profit !== undefined ||
                                      objective.lambda_avg_price !== undefined ||
                                      objective.lambda_price_slack !== undefined ||
                                      // objective.lambda_tf_mix !== undefined ||
                                      preparation.expected_sellout !== undefined ||
                                      solver.type !== undefined ||
                                      solver.time_limit !== undefined;
                    
                    return hasConfigs ? (
                      <div className="relative">
                        <h4 className="absolute -top-3 right-2 text-sm font-semibold text-gray-900 px-2" style={{ backgroundColor: '#E8EDF2' }}>CONFIGURAZIONI</h4>
                        <div className="border border-black p-3 pt-4" style={{ backgroundColor: '#E8EDF2' }}>
                          <div className="space-y-3">
                            {/* Objective Section */}
                            {(objective.lambda_plus !== undefined || objective.lambda_minus !== undefined || objective.rho_profit !== undefined || objective.lambda_avg_price !== undefined || objective.lambda_price_slack !== undefined || objective.lambda_tf_mix !== undefined) && (
                              <div>
                                <div className="text-xs font-bold text-gray-700 uppercase mb-1">Objective</div>
                                <div className="grid grid-cols-3 gap-2">
                                  {objective.lambda_plus !== undefined && (
                                    <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                      <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">λ+</div>
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={objective.lambda_plus}
                                        onChange={async (e) => {
                                          const newValue = parseFloat(e.target.value);
                                          if (isNaN(newValue)) return;
                                          const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                          if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                          updatedConstraints.objective.lambda_plus = newValue;
                                          setConstraints(updatedConstraints);
                                          
                                          try {
                                            const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                            if (tempThreadId && messageId) {
                                              await performAction({
                                                threadId: tempThreadId,
                                                messageId,
                                                actionName: "optim-update-config",
                                                params: { "optim-config-data": updatedConstraints },
                                              });
                                            }
                                          } catch (err) {
                                            console.error("Error updating lambda_plus:", err);
                                          }
                                        }}
                                        className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                      />
                                    </div>
                                  )}
                                  {objective.lambda_minus !== undefined && (
                                    <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                      <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">λ−</div>
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={objective.lambda_minus}
                                        onChange={async (e) => {
                                          const newValue = parseFloat(e.target.value);
                                          if (isNaN(newValue)) return;
                                          const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                          if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                          updatedConstraints.objective.lambda_minus = newValue;
                                          setConstraints(updatedConstraints);
                                          
                                          try {
                                            const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                            if (tempThreadId && messageId) {
                                              await performAction({
                                                threadId: tempThreadId,
                                                messageId,
                                                actionName: "optim-update-config",
                                                params: { "optim-config-data": updatedConstraints },
                                              });
                                            }
                                          } catch (err) {
                                            console.error("Error updating lambda_minus:", err);
                                          }
                                        }}
                                        className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                      />
                                    </div>
                                  )}
                                  {objective.rho_profit !== undefined && (
                                    <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                      <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">ρ Profit</div>
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={objective.rho_profit}
                                        onChange={async (e) => {
                                          const newValue = parseFloat(e.target.value);
                                          if (isNaN(newValue)) return;
                                          const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                          if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                          updatedConstraints.objective.rho_profit = newValue;
                                          setConstraints(updatedConstraints);
                                          
                                          try {
                                            const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                            if (tempThreadId && messageId) {
                                              await performAction({
                                                threadId: tempThreadId,
                                                messageId,
                                                actionName: "optim-update-config",
                                                params: { "optim-config-data": updatedConstraints },
                                              });
                                            }
                                          } catch (err) {
                                            console.error("Error updating rho_profit:", err);
                                          }
                                        }}
                                        className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                      />
                                    </div>
                                  )}
                                  {objective.lambda_avg_price !== undefined && (
                                    <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                      <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">λ Avg Price</div>
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={objective.lambda_avg_price}
                                        onChange={async (e) => {
                                          const newValue = parseFloat(e.target.value);
                                          if (isNaN(newValue)) return;
                                          const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                          if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                          updatedConstraints.objective.lambda_avg_price = newValue;
                                          setConstraints(updatedConstraints);
                                          
                                          try {
                                            const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                            if (tempThreadId && messageId) {
                                              await performAction({
                                                threadId: tempThreadId,
                                                messageId,
                                                actionName: "optim-update-config",
                                                params: { "optim-config-data": updatedConstraints },
                                              });
                                            }
                                          } catch (err) {
                                            console.error("Error updating lambda_avg_price:", err);
                                          }
                                        }}
                                        className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                      />
                                    </div>
                                  )}
                                  {objective.lambda_price_slack !== undefined && (
                                    <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                      <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">λ Price Slack</div>
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={objective.lambda_price_slack}
                                        onChange={async (e) => {
                                          const newValue = parseFloat(e.target.value);
                                          if (isNaN(newValue)) return;
                                          const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                          if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                          updatedConstraints.objective.lambda_price_slack = newValue;
                                          setConstraints(updatedConstraints);

                                          try {
                                            const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                            if (tempThreadId && messageId) {
                                              await performAction({
                                                threadId: tempThreadId,
                                                messageId,
                                                actionName: "optim-update-config",
                                                params: { "optim-config-data": updatedConstraints },
                                              });
                                            }
                                          } catch (err) {
                                            console.error("Error updating lambda_price_slack:", err);
                                          }
                                        }}
                                        className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                      />
                                    </div>
                                  )}
                                  {objective.lambda_tf_mix !== undefined && (
                                    <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                      <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">λ TF Mix</div>
                                      <input
                                        type="number"
                                        step="0.1"
                                        value={objective.lambda_tf_mix}
                                        onChange={async (e) => {
                                          const newValue = parseFloat(e.target.value);
                                          if (isNaN(newValue)) return;
                                          const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                          if (!updatedConstraints.objective) updatedConstraints.objective = {};
                                          updatedConstraints.objective.lambda_tf_mix = newValue;
                                          setConstraints(updatedConstraints);

                                          try {
                                            const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                            if (tempThreadId && messageId) {
                                              await performAction({
                                                threadId: tempThreadId,
                                                messageId,
                                                actionName: "optim-update-config",
                                                params: { "optim-config-data": updatedConstraints },
                                              });
                                            }
                                          } catch (err) {
                                            console.error("Error updating lambda_tf_mix:", err);
                                          }
                                        }}
                                        className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Preparation and Solver Section */}
                            {(preparation.expected_sellout !== undefined || preparation.force_first_three_taglie !== undefined || preparation.taglie_sampling_method !== undefined || solver.type !== undefined || solver.time_limit !== undefined) && (
                              <div className="grid grid-cols-2 gap-3">
                                {/* Preparation Section */}
                                {(preparation.expected_sellout !== undefined || preparation.force_first_three_taglie !== undefined || preparation.taglie_sampling_method !== undefined) && (
                                  <div>
                                    <div className="text-xs font-bold text-gray-700 uppercase mb-1">Preparation</div>
                                    <div className="border border-gray-300 rounded px-2 py-2" style={{ backgroundColor: '#E8EDF2' }}>
                                      {preparation.expected_sellout !== undefined && (
                                        <>
                                          {/* Global Expected Sellout */}
                                          <div className="flex items-center justify-between mb-1">
                                            <div className="text-[9px] font-semibold text-gray-600 uppercase">Expected Sellout (Globale)</div>
                                            <div className="text-xs font-bold text-gray-900">
                                              {preparation.expected_sellout.toFixed(2)}
                                            </div>
                                          </div>
                                          <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            value={preparation.expected_sellout}
                                            onChange={async (e) => {
                                              const newValue = parseFloat(e.target.value);
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.preparation) {
                                                updatedConstraints.preparation = {};
                                              }
                                              updatedConstraints.preparation.expected_sellout = newValue;
                                              setConstraints(updatedConstraints);
                                              
                                              try {
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                if (tempThreadId && messageId) {
                                                  await performAction({
                                                    threadId: tempThreadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating expected_sellout:", err);
                                              }
                                            }}
                                            className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer"
                                            style={{
                                              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${preparation.expected_sellout * 100}%, #d1d5db ${preparation.expected_sellout * 100}%, #d1d5db 100%)`
                                            }}
                                          />
                                        </>
                                      )}
                                      
                                      {/* Per-Tipo-Filato Overrides */}
                                      {tipoFilatoOverrides.map((override) => (
                                        <div key={override.id} className="mt-2 pt-2 border-t border-gray-200">
                                          <div className="flex items-center justify-between mb-1 gap-1">
                                            <select
                                              value={override.tipo_filato}
                                              onChange={(e) => {
                                                const newOverrides = tipoFilatoOverrides.map(o =>
                                                  o.id === override.id ? { ...o, tipo_filato: e.target.value } : o
                                                );
                                                setTipoFilatoOverrides(newOverrides);
                                                updateConstraintsWithOverrides(newOverrides);
                                              }}
                                              className="text-[9px] px-1 py-0.5 border border-gray-300 rounded flex-1"
                                            >
                                              <option value="">(seleziona tipo filato)</option>
                                              {tipoFilatoEnum.map(tipo => (
                                                <option key={tipo} value={tipo}>{tipo}</option>
                                              ))}
                                            </select>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const newOverrides = tipoFilatoOverrides.filter(o => o.id !== override.id);
                                                setTipoFilatoOverrides(newOverrides);
                                                updateConstraintsWithOverrides(newOverrides);
                                              }}
                                              className="text-[9px] px-1.5 py-0.5 text-red-600 hover:text-red-800 border border-red-300 rounded"
                                            >
                                              ✕
                                            </button>
                                          </div>
                                          <div className="flex items-center justify-between mb-0.5">
                                            <div className="text-[9px] font-semibold text-gray-600">Sellout</div>
                                            <div className="text-xs font-bold text-gray-900">
                                              {override.value.toFixed(2)}
                                            </div>
                                          </div>
                                          <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            value={override.value}
                                            onChange={(e) => {
                                              const newValue = parseFloat(e.target.value);
                                              const newOverrides = tipoFilatoOverrides.map(o =>
                                                o.id === override.id ? { ...o, value: newValue } : o
                                              );
                                              setTipoFilatoOverrides(newOverrides);
                                              updateConstraintsWithOverrides(newOverrides);
                                            }}
                                            className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer"
                                            style={{
                                              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${override.value * 100}%, #d1d5db ${override.value * 100}%, #d1d5db 100%)`
                                            }}
                                          />
                                        </div>
                                      ))}
                                      
                                      {/* Add Override Button */}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const nextId = tipoFilatoOverrides.length > 0 
                                            ? Math.max(...tipoFilatoOverrides.map(o => o.id)) + 1 
                                            : 1;
                                          setTipoFilatoOverrides([
                                            ...tipoFilatoOverrides,
                                            { id: nextId, tipo_filato: '', value: 0.5 }
                                          ]);
                                        }}
                                        className="mt-2 w-full text-[9px] px-2 py-1 bg-white border border-gray-400 rounded hover:bg-gray-50"
                                      >
                                        + Specifica per Tipo Filato
                                      </button>
                                      
                                      {/* Scale Orders Toggle */}
                                      <div className="mt-3 pt-2 border-t border-gray-200">
                                        <label className="flex items-center justify-between cursor-pointer">
                                          <span className="text-[9px] font-semibold text-gray-600 uppercase">Scale Orders</span>
                                          <div className="relative">
                                            <input
                                              type="checkbox"
                                              checked={preparation.scale_orders || false}
                                              onChange={async (e) => {
                                                const newValue = e.target.checked;
                                                const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                if (!updatedConstraints.preparation) {
                                                  updatedConstraints.preparation = {};
                                                }
                                                updatedConstraints.preparation.scale_orders = newValue;
                                                setConstraints(updatedConstraints);
                                                
                                                try {
                                                  const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                  if (tempThreadId && messageId) {
                                                    await performAction({
                                                      threadId: tempThreadId,
                                                      messageId,
                                                      actionName: "optim-update-config",
                                                      params: { "optim-config-data": updatedConstraints },
                                                    });
                                                  }
                                                } catch (err) {
                                                  console.error("Error updating scale_orders:", err);
                                                }
                                              }}
                                              className="sr-only peer"
                                            />
                                            <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-500"></div>
                                          </div>
                                        </label>
                                      </div>

                                      {preparation.force_first_three_taglie !== undefined && (
                                        <div className="mt-2 pt-2 border-t border-gray-200">
                                          <label className="flex items-center justify-between cursor-pointer">
                                            <span className="text-[9px] font-semibold text-gray-600 uppercase">Force Three Taglie</span>
                                            <div className="relative">
                                              <input
                                                type="checkbox"
                                                checked={preparation.force_first_three_taglie}
                                                onChange={async (e) => {
                                                  const newValue = e.target.checked;
                                                  const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                  if (!updatedConstraints.preparation) {
                                                    updatedConstraints.preparation = {};
                                                  }
                                                  updatedConstraints.preparation.force_first_three_taglie = newValue;
                                                  setConstraints(updatedConstraints);

                                                  try {
                                                    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                    if (tempThreadId && messageId) {
                                                      await performAction({
                                                        threadId: tempThreadId,
                                                        messageId,
                                                        actionName: "optim-update-config",
                                                        params: { "optim-config-data": updatedConstraints },
                                                      });
                                                    }
                                                  } catch (err) {
                                                    console.error("Error updating force_first_three_taglie:", err);
                                                  }
                                                }}
                                                className="sr-only peer"
                                              />
                                              <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-500"></div>
                                            </div>
                                          </label>
                                        </div>
                                      )}

                                      {preparation.taglie_sampling_method !== undefined && (
                                        <div className="mt-2 pt-2 border-t border-gray-200">
                                          <div className="flex items-center justify-between gap-2">
                                            <span className="text-[9px] font-semibold text-gray-600 uppercase">Taglie Sampling</span>
                                            <select
                                              value={preparation.taglie_sampling_method}
                                              onChange={async (e) => {
                                                const newValue = e.target.value;
                                                const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                                if (!updatedConstraints.preparation) {
                                                  updatedConstraints.preparation = {};
                                                }
                                                updatedConstraints.preparation.taglie_sampling_method = newValue;
                                                setConstraints(updatedConstraints);

                                                try {
                                                  const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                  if (tempThreadId && messageId) {
                                                    await performAction({
                                                      threadId: tempThreadId,
                                                      messageId,
                                                      actionName: "optim-update-config",
                                                      params: { "optim-config-data": updatedConstraints },
                                                    });
                                                  }
                                                } catch (err) {
                                                  console.error("Error updating taglie_sampling_method:", err);
                                                }
                                              }}
                                              className="text-[9px] px-2 py-1 border border-gray-300 rounded bg-white"
                                            >
                                              <option value="legacy">legacy</option>
                                              <option value="hare_niemeyer">hare_niemeyer</option>
                                            </select>
                                          </div>
                                        </div>
                                      )}
                                      
                                    </div>
                                  </div>
                                )}
                                
                                {/* Solver Section */}
                                {(solver.type !== undefined || solver.time_limit !== undefined) && (
                                  <div>
                                    <div className="text-xs font-bold text-gray-700 uppercase mb-1">Solver</div>
                                    <div className="flex gap-2">
                                      {solver.type !== undefined && (
                                        <div className="border border-gray-300 rounded px-2 py-1 flex-1" style={{ backgroundColor: '#E8EDF2' }}>
                                          <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">Type</div>
                                          <select
                                            value={solver.type}
                                            onChange={async (e) => {
                                              const newValue = e.target.value;
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.solver) {
                                                updatedConstraints.solver = {};
                                              }
                                              updatedConstraints.solver.type = newValue;
                                              setConstraints(updatedConstraints);
                                              
                                              try {
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                if (tempThreadId && messageId) {
                                                  await performAction({
                                                    threadId: tempThreadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating solver type:", err);
                                              }
                                            }}
                                            className="w-full text-xs font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                          >
                                            <option value="CBC">CBC</option>
                                            <option value="CPSAT">CPSAT</option>
                                          </select>
                                        </div>
                                      )}
                                      {solver.time_limit !== undefined && (
                                        <div className="border border-gray-300 rounded px-2 py-1 flex-1" style={{ backgroundColor: '#E8EDF2' }}>
                                          <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">Time Limit (s)</div>
                                          <input
                                            type="number"
                                            step="1"
                                            min="0"
                                            value={solver.time_limit}
                                            onChange={async (e) => {
                                              const newValue = parseInt(e.target.value);
                                              if (isNaN(newValue) || newValue < 0) return;
                                              const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                              if (!updatedConstraints.solver) updatedConstraints.solver = {};
                                              updatedConstraints.solver.time_limit = newValue;
                                              setConstraints(updatedConstraints);
                                              
                                              try {
                                                const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                                if (tempThreadId && messageId) {
                                                  await performAction({
                                                    threadId: tempThreadId,
                                                    messageId,
                                                    actionName: "optim-update-config",
                                                    params: { "optim-config-data": updatedConstraints },
                                                  });
                                                }
                                              } catch (err) {
                                                console.error("Error updating time_limit:", err);
                                              }
                                            }}
                                            className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {/* PARAMETRI AGGIUNTIVI Section */}
                  {constraints && (() => {
                    const constraintsData = constraints.constraints || {};
                    
                    const hasTotalPieces = constraintsData.total_pieces !== undefined;
                    const hasNonMasterShare = constraintsData.non_master_share !== undefined;
                    const hasMinAllowedValue = constraintsData.min_allowed_value !== undefined;
                    const hasMinAllowedValuePerTipoFilato = constraintsData.min_allowed_value_per_tipo_filato !== undefined;
                    const hasMinActiveSkus = constraintsData.min_active_skus !== undefined;
                    const hasActiveConstraints = constraintsData.active_constraints !== undefined;
                    const hasForecastBias = constraintsData.forecast_bias !== undefined;
                    const hasPercentageColored = constraintsData.percentage_colored !== undefined;
                    const hasMinTotalQuantitiesForSplit = constraintsData.min_total_quantities_for_split !== undefined;
                    const hasParams = hasTotalPieces || hasNonMasterShare || hasMinAllowedValue || hasMinAllowedValuePerTipoFilato || hasMinActiveSkus || hasActiveConstraints || hasForecastBias || hasPercentageColored || hasMinTotalQuantitiesForSplit;
                    
                    return hasParams ? (
                      <div className="relative">
                        <h4 className="absolute -top-3 right-2 text-sm font-semibold text-gray-900 px-2" style={{ backgroundColor: '#E8EDF2' }}>PARAMETRI AGGIUNTIVI</h4>
                        <div className="border border-black p-3 pt-4" style={{ backgroundColor: '#E8EDF2' }}>
                          <div className="grid grid-cols-3 gap-2">
                            {/* Total Pieces - Checkbox */}
                            {hasTotalPieces && (
                              <div className="flex items-center justify-between border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                                <label className="text-xs font-semibold text-gray-700 uppercase cursor-pointer">
                                  Total Pieces
                                </label>
                                <input
                                  type="checkbox"
                                  checked={constraintsData.total_pieces}
                                  onChange={async (e) => {
                                    const newValue = e.target.checked;
                                    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                    if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                    updatedConstraints.constraints.total_pieces = newValue;
                                    setConstraints(updatedConstraints);
                                    
                                    try {
                                      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                      if (tempThreadId && messageId) {
                                        await performAction({
                                          threadId: tempThreadId,
                                          messageId,
                                          actionName: "optim-update-config",
                                          params: { "optim-config-data": updatedConstraints },
                                        });
                                      }
                                    } catch (err) {
                                      console.error("Error updating total_pieces:", err);
                                    }
                                  }}
                                  className="w-4 h-4 text-[#9F6460] border-gray-300 rounded focus:ring-[#9F6460]"
                                />
                              </div>
                            )}
                            
                            {/* Non-Master Share - Numeric */}
                            {hasNonMasterShare && (
                              <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">Non-Master Share</div>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="1"
                                  value={constraintsData.non_master_share}
                                  onChange={async (e) => {
                                    const newValue = parseFloat(e.target.value);
                                    if (isNaN(newValue)) return;
                                    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                    if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                    updatedConstraints.constraints.non_master_share = newValue;
                                    setConstraints(updatedConstraints);
                                    
                                    try {
                                      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                      if (tempThreadId && messageId) {
                                        await performAction({
                                          threadId: tempThreadId,
                                          messageId,
                                          actionName: "optim-update-config",
                                          params: { "optim-config-data": updatedConstraints },
                                        });
                                      }
                                    } catch (err) {
                                      console.error("Error updating non_master_share:", err);
                                    }
                                  }}
                                  className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                />
                              </div>
                            )}
                            
                            {/* Min Allowed Value - Numeric */}
                            {hasMinAllowedValue && (
                              <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">Min Allowed Value</div>
                                <input
                                  type="number"
                                  step="1"
                                  min="0"
                                  value={constraintsData.min_allowed_value}
                                  onChange={async (e) => {
                                    const newValue = parseInt(e.target.value);
                                    if (isNaN(newValue)) return;
                                    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                    if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                    updatedConstraints.constraints.min_allowed_value = newValue;
                                    setConstraints(updatedConstraints);
                                    
                                    try {
                                      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                      if (tempThreadId && messageId) {
                                        await performAction({
                                          threadId: tempThreadId,
                                          messageId,
                                          actionName: "optim-update-config",
                                          params: { "optim-config-data": updatedConstraints },
                                        });
                                      }
                                    } catch (err) {
                                      console.error("Error updating min_allowed_value:", err);
                                    }
                                  }}
                                  className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                />
                              </div>
                            )}

                            {hasMinAllowedValuePerTipoFilato && (
                              <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">Min Value per Tipo Filato</div>
                                <div className="max-h-80 overflow-y-auto pr-1">
                                  {minAllowedValueTipoFilatoOverrides.map((override) => (
                                    <div key={override.id} className="mt-2 pt-2 first:mt-0 first:pt-0 border-t first:border-t-0 border-gray-200">
                                      <div className="flex items-center justify-between mb-1 gap-1">
                                        <select
                                          value={override.tipo_filato}
                                          onChange={(e) => {
                                            const newOverrides = minAllowedValueTipoFilatoOverrides.map((item) =>
                                              item.id === override.id ? { ...item, tipo_filato: e.target.value } : item
                                            );
                                            setMinAllowedValueTipoFilatoOverrides(newOverrides);
                                            updateMinAllowedValuePerTipoFilatoOverrides(newOverrides);
                                          }}
                                          className="text-[9px] px-1 py-0.5 border border-gray-300 rounded flex-1"
                                        >
                                          <option value="">(seleziona tipo filato)</option>
                                          {tipoFilatoEnum.map((tipo) => (
                                            <option key={tipo} value={tipo}>{tipo}</option>
                                          ))}
                                        </select>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newOverrides = minAllowedValueTipoFilatoOverrides.filter((item) => item.id !== override.id);
                                            setMinAllowedValueTipoFilatoOverrides(newOverrides);
                                            updateMinAllowedValuePerTipoFilatoOverrides(newOverrides);
                                          }}
                                          className="text-[9px] px-1.5 py-0.5 text-red-600 hover:text-red-800 border border-red-300 rounded"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                      <input
                                        type="number"
                                        step="1"
                                        min="0"
                                        value={override.value}
                                        onChange={(e) => {
                                          const newValue = parseInt(e.target.value, 10);
                                          if (isNaN(newValue) || newValue < 0) return;
                                          const newOverrides = minAllowedValueTipoFilatoOverrides.map((item) =>
                                            item.id === override.id ? { ...item, value: newValue } : item
                                          );
                                          setMinAllowedValueTipoFilatoOverrides(newOverrides);
                                          updateMinAllowedValuePerTipoFilatoOverrides(newOverrides);
                                        }}
                                        className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                      />
                                    </div>
                                  ))}
                                </div>

                                <button
                                  type="button"
                                  onClick={() => {
                                    const nextId = minAllowedValueTipoFilatoOverrides.length > 0
                                      ? Math.max(...minAllowedValueTipoFilatoOverrides.map((item) => item.id)) + 1
                                      : 1;
                                    setMinAllowedValueTipoFilatoOverrides([
                                      ...minAllowedValueTipoFilatoOverrides,
                                      { id: nextId, tipo_filato: '', value: constraintsData.min_allowed_value ?? 3 },
                                    ]);
                                  }}
                                  className="mt-2 w-full text-[9px] px-2 py-1 bg-white border border-gray-400 rounded hover:bg-gray-50"
                                >
                                  + Specifica per Tipo Filato
                                </button>
                              </div>
                            )}
                            
                            {/* Min Active SKUs - Numeric */}
                            {hasMinActiveSkus && (
                              <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">Min Active SKUs</div>
                                <input
                                  type="number"
                                  step="1"
                                  min="0"
                                  value={constraintsData.min_active_skus}
                                  onChange={async (e) => {
                                    const newValue = parseInt(e.target.value);
                                    if (isNaN(newValue)) return;
                                    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                    if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                    updatedConstraints.constraints.min_active_skus = newValue;
                                    setConstraints(updatedConstraints);
                                    
                                    try {
                                      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                      if (tempThreadId && messageId) {
                                        await performAction({
                                          threadId: tempThreadId,
                                          messageId,
                                          actionName: "optim-update-config",
                                          params: { "optim-config-data": updatedConstraints },
                                        });
                                      }
                                    } catch (err) {
                                      console.error("Error updating min_active_skus:", err);
                                    }
                                  }}
                                  className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                />
                              </div>
                            )}
                            
                            {/* Forecast Bias - Numeric */}
                            {hasForecastBias && (
                              <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">Forecast Bias</div>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={constraintsData.forecast_bias}
                                  onChange={async (e) => {
                                    const newValue = parseFloat(e.target.value);
                                    if (isNaN(newValue)) return;
                                    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                    if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                    updatedConstraints.constraints.forecast_bias = newValue;
                                    setConstraints(updatedConstraints);
                                    
                                    try {
                                      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                      if (tempThreadId && messageId) {
                                        await performAction({
                                          threadId: tempThreadId,
                                          messageId,
                                          actionName: "optim-update-config",
                                          params: { "optim-config-data": updatedConstraints },
                                        });
                                      }
                                    } catch (err) {
                                      console.error("Error updating forecast_bias:", err);
                                    }
                                  }}
                                  className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                />
                              </div>
                            )}
                            
                            {/* Percentage Colored - Numeric */}
                            {hasPercentageColored && (
                              <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">% Colored</div>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="1"
                                  value={constraintsData.percentage_colored}
                                  onChange={async (e) => {
                                    const newValue = parseFloat(e.target.value);
                                    if (isNaN(newValue)) return;
                                    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                    if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                    updatedConstraints.constraints.percentage_colored = newValue;
                                    setConstraints(updatedConstraints);
                                    
                                    try {
                                      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                      if (tempThreadId && messageId) {
                                        await performAction({
                                          threadId: tempThreadId,
                                          messageId,
                                          actionName: "optim-update-config",
                                          params: { "optim-config-data": updatedConstraints },
                                        });
                                      }
                                    } catch (err) {
                                      console.error("Error updating percentage_colored:", err);
                                    }
                                  }}
                                  className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                />
                              </div>
                            )}
                            
                            {/* Min Total Quantities for Split - Numeric */}
                            {hasMinTotalQuantitiesForSplit && (
                              <div className="border border-gray-300 rounded px-2 py-1" style={{ backgroundColor: '#E8EDF2' }}>
                                <div className="text-[9px] font-semibold text-gray-600 uppercase mb-1">Min Qty for Split</div>
                                <input
                                  type="number"
                                  step="1"
                                  min="0"
                                  value={constraintsData.min_total_quantities_for_split}
                                  onChange={async (e) => {
                                    const newValue = parseInt(e.target.value);
                                    if (isNaN(newValue)) return;
                                    const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                    if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                    updatedConstraints.constraints.min_total_quantities_for_split = newValue;
                                    setConstraints(updatedConstraints);
                                    
                                    try {
                                      const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                      if (tempThreadId && messageId) {
                                        await performAction({
                                          threadId: tempThreadId,
                                          messageId,
                                          actionName: "optim-update-config",
                                          params: { "optim-config-data": updatedConstraints },
                                        });
                                      }
                                    } catch (err) {
                                      console.error("Error updating min_total_quantities_for_split:", err);
                                    }
                                  }}
                                  className="w-full text-sm font-bold text-gray-900 bg-white border border-gray-400 rounded px-2 py-1"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {/* VINCOLI ATTIVI Section */}
                  {constraints?.constraints?.active_constraints !== undefined && (
                    <div className="relative">
                      <h4 className="absolute -top-3 right-2 text-sm font-semibold text-gray-900 px-2" style={{ backgroundColor: '#E8EDF2' }}>VINCOLI ATTIVI</h4>
                      <div className="border border-black p-3 pt-4" style={{ backgroundColor: '#E8EDF2' }}>
                        <div className="grid grid-cols-4 gap-2">
                          {Object.entries(constraints.constraints.active_constraints || {}).filter(([key]) => key !== 'balanced_share_soft').map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between border border-gray-300 rounded px-2 py-1.5" style={{ backgroundColor: '#E8EDF2' }}>
                              <label
                                className="text-[9px] font-semibold text-gray-700 uppercase cursor-pointer truncate mr-2"
                                title={key === 'budget_target_soft'
                                  ? 'Preferenza soft: distribuisce le assegnazioni tra le consegne seguendo le quote del budget del negozio (valore economico se e\' attivo "budget", pezzi se e\' attivo "budget items"). Non vieta nulla. Peso: lambda_budget_target.'
                                  : undefined}
                              >
                                {key.replace(/_/g, ' ')}
                              </label>
                              <input
                                type="checkbox"
                                checked={value as boolean}
                                onChange={async (e) => {
                                  const newValue = e.target.checked;
                                  const updatedConstraints = JSON.parse(JSON.stringify(constraints));
                                  if (!updatedConstraints.constraints) updatedConstraints.constraints = {};
                                  if (!updatedConstraints.constraints.active_constraints) updatedConstraints.constraints.active_constraints = {};
                                  updatedConstraints.constraints.active_constraints[key] = newValue;
                                  setConstraints(updatedConstraints);
                                  
                                  try {
                                    const messageId = thread.threadData?.local_memory?.thread_state?.["current-message"] as string;
                                    if (tempThreadId && messageId) {
                                      await performAction({
                                        threadId: tempThreadId,
                                        messageId,
                                        actionName: "optim-update-config",
                                        params: { "optim-config-data": updatedConstraints },
                                      });
                                    }
                                  } catch (err) {
                                    console.error(`Error updating active_constraints.${key}:`, err);
                                  }
                                }}
                                className="w-4 h-4 text-[#9F6460] border-gray-300 rounded focus:ring-[#9F6460] flex-shrink-0"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <p className="text-sm text-gray-500">Nessuna configurazione disponibile</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-4 py-3 border-t border-gray-300 bg-white">
              <button
                type="button"
                onClick={() => setShowConfigPopup(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                CHIUDI
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowConfigPopup(false);
                  proceedWithProjectCreation();
                }}
                className="px-6 py-2 bg-black text-white hover:bg-gray-800 transition-colors"
              >
                PROCEDI CON OTTIMIZZAZIONE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
